import * as _ from 'lodash';
import ___default from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import slugify from 'slugify';

// Maps the identifiers used in the parameters to the identifiers used internally.
const EXPORT_LOAD_PARSING_IDENTIFIER_MAPPING = {
    incident: "fault",
    date_lancement: "day_date",
    quantite_op: "load",
    pdc: "erp_id",
};
const EXPORT_PROD_PARSING_IDENTIFIER_MAPPING = {
    date_production: "date",
    quantite_produite: "qte",
};
// Identifiers that should always be exported.
const EXPORT_BYPASS_MAPPING = ["fast_track", "tags"];
const EXPORT_LOAD_BYPASS_MAPPING = [
    ...EXPORT_BYPASS_MAPPING,
    // Generated columns
    "date_erp_op",
    "date_erp_of",
    "date_planifiee_op",
    "date_planifiee_of",
    "date_fin_estimee_of",
    "avance_retard_of",
    "poste_de_charge",
    "date_heure_changement_statut",
    "date_heure_derniere_mise_a_jour",
    "status_available_at",
    "status_ongoing_at",
    "status_done_at",
    "last_comment",
    // Only useful for VCA (see OPL-6349)
    "article",
    "incident",
];
const EXPORT_PROD_BYPASS_MAPPING = [
    ...EXPORT_BYPASS_MAPPING,
    // Generated columns
    "import_date",
];
/**
 * Maps the exported identifiers (as received from the back-end) to the previously
 * standardized names (before OPL-6082, see simulations/helpers.ts).
 */
const EXPORT_SCHEDULING_LOAD_DEFAULT_MAPPING = {
    op_id: "num_op",
    of_id: "num_of",
    op_name: "operation",
    op_sequence: "num_operation",
    ref_article: "article",
    quantite_of: "quantite",
    order_number: "numero_commande",
    load: "charge",
    erp_date: "date_erp_op",
    day_date: "date_planifiee_op",
    tags: "tags",
    fault: "incident",
    op_status: "statut",
    quantity_produced: "quantity_produced",
};

const MAIN_OPLIT_MODULES = [
    "has_module_pdp",
    "has_module_scheduling",
    "has_module_stocks",
    "has_module_customer_interface_provider_side",
];
const SECONDARY_OPLIT_MODULES = [
    "has_module_customer_interface_customer_side",
];
const ALL_OPLIT_MODULES = [...MAIN_OPLIT_MODULES, ...SECONDARY_OPLIT_MODULES];
const MAP_TABLE_MODULE_ROUTE_NAME = {
    has_module_customer_interface_customer_side: "customer-interface",
};
const DATE_DEFAULT_FORMAT = "YYYY-MM-DD";
const DATE_WITH_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss.SSS";
const TIME_DEFAULT_FORMAT = "HH:mm";
const DATE_WITH_DEFAULT_TIME_FORMAT = "YYYY-MM-DD HH:mm";
const DATE_EXPORT_TO_ERP_FORMAT = "DD/MM/YYYY";
const DATE_WITH_TIME_EXPORT_TO_ERP_FORMAT = "DD/MM/YYYY HH:mm:ss";
const DEFAULT_PAST_START_DATE = "1970-01-01";
const TIME_WITH_SECONDS_FORMAT = "HH:mm:ss";
const DATE_FORMATS = {
    FR: "DD/MM/YYYY",
};
const OF_STATUS = {
    DONE: "done",
    ON_GOING: "on-going",
    AVAILABLE: "available",
    PENDING: "pending",
    POF: "pof",
};
const FAST_TRACK = {
    P1: "P1",
    P2: "P2",
    P3: "P3",
    P4: "P4",
    P5: "P5",
    P6: "P6",
    P7: "P7",
    P8: "P8",
    P9: "P9",
};
const DEFAULT_LOAD_UNITS = {
    OF: "pièces",
    OP: "heures",
    OP_2: "€",
};
const LOCALES = {
    FR: "fr",
    EN: "en",
};

const PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION = "__|__";
/**
 * replaces the concatenated field separator (as time of writing, __|__) that is added through
 * import parsing rules in specific portions of the app for a nicer readability for users
 */
function getReadableImportParsingRuleValue(value) {
    if (!value)
        return value;
    if (typeof value !== "string")
        return value;
    if (!value.includes(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION))
        return value;
    return value.replaceAll(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION, " - ");
}

const DEFAULT_TABLE_OF_HEADER_KEYS = [
    "of_id",
    "ref_article",
    "displayed_quantite",
    "customer",
    "order_number",
    "tags",
    "completed_ops",
    "current_op",
    "current_pdc_name",
    "nbAvanceRetard",
    "date",
    "real_theoric_date",
    "theoric_date",
    "min_oplit_date",
    "oplit_date",
    "max_oplit_date",
    "oplit_date_impact",
    "last_comment",
];
const TABLE_OF_NEEDED_SUBKEYS = {
    displayed_quantite: ["quantite_of", "quantite", "unit"],
    completed_ops: ["number_of_operations"],
    oplit_date: [
        "predicted_of_end_date",
        "remaining_of_planned_lead_time",
        "remaining_of_predicted_lead_time",
        "ops",
    ],
    current_pdc_name: ["all_of_machine_centers", "current_pdc_id"],
};
const ALL_TABLE_OF_HEADER_KEYS = [
    ...DEFAULT_TABLE_OF_HEADER_KEYS,
    ...Object.values(TABLE_OF_NEEDED_SUBKEYS).flat(),
];
const OFS_SEARCHABLE_FIELDS = {
    OF_ID: "of_id",
    REF_ARTICLE: "ref_article",
    CUSTOMER: "customer",
    ORDER_NUMBER: "order_number",
};
const PENDING_ORDERS_PARAMS = {
    COUNT_LIMIT: 10,
    OTHERS: -1,
};
const SCHEDULING_COMPONENTS_QUANTITIES_DECIMALS = 3;

const LOAD = {
    MANDATORY_COLUMNS: {
        MATCHING: ["erp_id", "of_id", "quantite_op", "date_lancement"],
        SCHEDULING: [
            "quantite_of",
            "op_id",
            "op_name",
            "op_status",
            "op_sequence",
            "ref_article",
        ],
    },
    TEMPLATE: [
        "erp_id",
        "op_id",
        "of_id",
        "quantite_op",
        "quantite_of",
        "date_lancement",
        "op_status",
        "op_sequence",
        "ref_article",
        "quantite_op_rebut",
        "quantite_of_besoin",
        "op_name",
        "unite_op",
        "unite_of",
        "quantite_op_2",
        "unite_op_2",
        "order_number",
        "customer",
        "tags",
        "op_tags",
        "incident",
        "fast_track",
        "op_fast_track",
    ],
};
const LOAD_STOCKS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["op_id", "ref_article", "quantite_of", "date_lancement"],
    },
    TEMPLATE: [
        "op_id",
        "quantite_of",
        "unite_of",
        "quantite_op_2",
        "unite_op_2",
        "date_lancement",
        "ref_article",
        "order_number",
        "customer",
        "famille",
    ],
};
const PROD = {
    MANDATORY_COLUMNS: {
        MATCHING: ["erp_id", "op_id"],
        SCHEDULING: ["op_status", "of_id"], // "quantite_of"
    },
    TEMPLATE: [
        "erp_id",
        "of_id",
        "quantite_produite",
        "date_production",
        "op_id",
        "quantite_of",
        "quantite_op_produite",
        "quantite_op_rebut",
        "op_status",
        "ref_article",
    ],
};
const PROD_STOCKS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["erp_id", "op_id", "quantite_produite", "date_production"],
    },
    TEMPLATE: [
        "erp_id",
        // "of_id",
        "quantite_produite",
        "date_production",
        "op_id",
        "quantite_op_rebut",
        // "quantite_of",
        // "quantite_op_produite",
        // "op_status",
        "ref_article",
    ],
};
const GAMMES = {
    MANDATORY_COLUMNS: {
        MATCHING: ["ref_article", "num_sequence", "erp_id"],
    },
    TEMPLATE: [
        "ref_article",
        "num_sequence",
        "op_name",
        "erp_id",
        "duration",
        "valorisation",
        "stock_cible",
        "stock_min",
        "stock_max",
        "famille",
        "leadtime",
    ],
};
const NOMENCLATURES = {
    MANDATORY_COLUMNS: {
        MATCHING: ["parent_article_id", "child_article_id", "coefficient"],
    },
    TEMPLATE: [
        "parent_article_id",
        "child_article_id",
        "coefficient",
        "leadtime",
    ],
};
const CMJ = {
    MANDATORY_COLUMNS: {
        MATCHING: ["ref_article", "cmj"],
    },
    TEMPLATE: ["ref_article", "cmj"],
};
const FP_STOCKS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["ref_article", "quantite"],
    },
    TEMPLATE: ["ref_article", "quantite"],
};
const WIP_STOCKS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["erp_id", "ref_article", "num_sequence", "quantite"],
    },
    TEMPLATE: ["erp_id", "ref_article", "num_sequence", "quantite"],
};
const PURCHASE_ORDERS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["id", "ref_article", "quantite", "availability_date"],
    },
    TEMPLATE: [
        "id",
        "ref_article",
        "quantite",
        "availability_date",
        "delivery_date",
        "order_number",
        "famille",
        "target_ref_article",
    ],
};
const CUSTOMERS_ORDERS = {
    MANDATORY_COLUMNS: {
        MATCHING: ["id", "ref_article", "quantite", "day_date"],
    },
    TEMPLATE: [
        "id",
        "ref_article",
        "quantite",
        "day_date",
        "unite",
        "order_number",
        "customer",
        "famille",
        "quantite_op_2",
        "unite_op_2",
    ],
};
const TECHNO_FAMILIES = {
    MANDATORY_COLUMNS: {
        MATCHING: ["ref_article", "techno_family"],
    },
    TEMPLATE: ["ref_article", "techno_family", "family"],
};
const COMPONENTS_INITIAL_STOCK = {
    MANDATORY_COLUMNS: {
        MATCHING: ["ref_article", "initial_stock"],
    },
    TEMPLATE: ["ref_article", "initial_stock"],
};
const IMPORT = {
    LOAD,
    LOAD_STOCKS,
    PROD,
    PROD_STOCKS,
    GAMMES,
    NOMENCLATURES,
    CMJ,
    FP_STOCKS,
    WIP_STOCKS,
    PURCHASE_ORDERS,
    CUSTOMERS_ORDERS,
    TECHNO_FAMILIES,
    COMPONENTS_INITIAL_STOCK,
};
const IMPORT_PARSING_FUNCTIONS = {
    MIN: "Math.min",
    MAX: "Math.max",
    CONCAT: `((...args) => args.join("${PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION}"))`,
    OR: `((...args) => args.find((arg) => !["", null, undefined].includes(arg)))`,
    REPLACE: `((input, target = "", replacement = "") => ("" + input).replaceAll(target, replacement))`,
};
const IMPORT_PARSING_OPERATIONS = ["+", "-", "x", "/", ","];
const IMPORT_PARSING_FIELD_FORMULA_ITEM_TYPES = [
    "operation",
    "function",
    "constant",
    "field",
    "parenthese_ouvrante",
    "parenthese_fermante",
];
const OPLIT_SUPPORT_EMAIL = "support@oplit.fr";
const IMPORT_TYPES = {
    LOAD: "ofs",
    PROD: "prod",
    PROD_PF: "prod_pf",
    PROD_ST: "prod_st",
    STOCK: "initial_stock",
    STOCK_PF: "initial_stock_pf",
    GAMMES: "gammes",
    NOMENCLATURES: "nomenclatures",
    CMJ: "cmj",
    PURCHASE_ORDERS: "purchase_orders",
    CUSTOMERS_ORDERS: "customers_orders",
    TECHNO_FAMILIES: "techno_families",
    COMPONENTS_INITIAL_STOCK: "components_initial_stock",
};

const USER_ROLES = {
    CLIENT_USER: "CLIENT_USER",
    CLIENT_ADMIN: "CLIENT_ADMIN",
    CLIENT_SUPER_ADMIN: "CLIENT_SUPER_ADMIN",
    GROUP_ADMIN: "GROUP_ADMIN",
    ADMIN: "ADMIN",
};

const EVENTS_NEW_ID_PREFIX = "new_of_";
var PdpLoadEventStatus;
(function (PdpLoadEventStatus) {
    PdpLoadEventStatus["ACTIVE"] = "active";
    PdpLoadEventStatus["DISABLED"] = "disabled";
    PdpLoadEventStatus["REMOVED"] = "removed";
})(PdpLoadEventStatus || (PdpLoadEventStatus = {}));
var PdpLoadEventType;
(function (PdpLoadEventType) {
    PdpLoadEventType["ADDITION"] = "addition";
    PdpLoadEventType["OVERWRITE"] = "overwrite";
    PdpLoadEventType["MODIFICATION"] = "modification";
    PdpLoadEventType["DELETION"] = "deletion";
    PdpLoadEventType["TRANSFER"] = "transfer";
})(PdpLoadEventType || (PdpLoadEventType = {}));

/**
 * represents the maximum target number of rows to treat for each iteration of a prod. obj. adjustment request
 */
const MAX_OPERATIONS_PER_BATCH = 50000;
const STOCKS_PROD_OBJ_TYPES = {
    DEMAND: "demand",
    TARGET_STOCK: "target_stock",
    WEIGHTED_TARGET_STOCK: "weighted_target_stock",
    MANUAL: "manual",
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

//This is a simple implementation of the type of DailyRawLoadNew, just making sure that we always have the same object, and that number are not strings for ex
const prepareDailyRawLoadData = (rawData) => {
    const { client_id, import_id, secteur_id, op_id, of_id, day_date, load, collection, type, updated_at, from_children, op_sequence, op_status, raw_op_id, ref_article, ref_client, max_date_of, quantity_produced, op_name, famille, tags, op_tags, fault, quantite_op_2, quantite_of, quantite_of_besoin, quantite_cumul_rebut, quantite_op_rebut, quantite_of_disponible, fast_track, op_fast_track, order_number, customer, is_new, data_source, status_done_at, status_ongoing_at, status_available_at, prod_updated_at, is_late, internal_extra_infos, extra_infos, partition_key = "active", target_ref_article, import_type = "ofs", } = rawData;
    const cleanData = {
        client_id,
        import_id,
        secteur_id,
        op_id: op_id || "",
        of_id: of_id || op_id || "",
        day_date,
        load: +(load || 0),
        collection,
        type,
        updated_at,
        from_children: !!from_children,
        op_sequence: op_sequence === null || op_sequence === undefined
            ? 999
            : +(op_sequence || 0),
        op_status,
        raw_op_id,
        ref_article,
        ref_client,
        max_date_of,
        quantity_produced: +(quantity_produced || 0),
        op_name,
        famille,
        tags,
        op_tags,
        fault,
        quantite_op_2: +(quantite_op_2 || 0),
        quantite_of: +(quantite_of || 0),
        quantite_of_besoin: +(quantite_of_besoin || 0),
        quantite_cumul_rebut: +(quantite_cumul_rebut || 0),
        quantite_op_rebut: +(quantite_op_rebut || 0),
        quantite_of_disponible: quantite_of_disponible != null ? +quantite_of_disponible : null,
        fast_track,
        op_fast_track,
        order_number,
        customer,
        is_new: !!is_new,
        data_source: data_source || "bigquery",
        status_done_at,
        status_ongoing_at,
        status_available_at,
        prod_updated_at,
        is_late: !!is_late,
        internal_extra_infos: internal_extra_infos || {},
        extra_infos: extra_infos || {},
        partition_key,
        target_ref_article,
        import_type,
    };
    Object.keys(cleanData).forEach((key) => {
        if (cleanData[key] === undefined)
            cleanData[key] = null; //This is to avoid keys being deleted in a JSON.stringify() or when sent to server
    });
    return cleanData;
};
const dailyRawLoadKeys = Object.keys(prepareDailyRawLoadData({}));
const dailyRawLoadPrimaryKeysFields = [
    "client_id",
    "import_id",
    "secteur_id",
    "op_id",
    "day_date",
    "partition_key",
];
const updatableDailyRawLoadKeys = dailyRawLoadKeys.filter((key) => !dailyRawLoadPrimaryKeysFields.includes(key));
const rawLoadInternalExtraInfosKeys = [
    "of_name",
    "is_weight",
    "unite_op",
    "unite_of",
    "datesortie",
    "retard",
    "machine_tag_id",
    "op_duration",
    "pdc",
    "real_datelancement",
    "initial_date",
    "pdp_ignore_is_late",
    "erp_status",
    "quantite_op",
];
//This is a simple implementation of the type of DailyLoadNew, just making sure that we always have the same object, and that number are not strings for ex
const prepareDailyLoadData = (rawData) => {
    const rawPreparedData = _.omit(prepareDailyRawLoadData(rawData), ["tags", "op_tags"]);
    const { simulation_id, macro_event_id, op_order, tags, op_tags, status_done_at, status_ongoing_at, status_available_at, status_updated_at, prod_updated_at, erp_date, prod_target, } = rawData;
    const { client_id, import_id, secteur_id, op_id, of_id, day_date, load, collection, type, updated_at, from_children, op_sequence, op_status, raw_op_id, ref_article, ref_client, max_date_of, quantity_produced, op_name, famille, fault, quantite_op_2, quantite_of, fast_track, op_fast_track, order_number, customer, is_new = false, data_source, is_late, internal_extra_infos, extra_infos, partition_key, target_ref_article, import_type, quantite_of_besoin, quantite_cumul_rebut, quantite_op_rebut, quantite_of_disponible, } = rawPreparedData;
    const cleanData = {
        client_id,
        import_id,
        simulation_id,
        secteur_id,
        op_id,
        of_id,
        day_date,
        load,
        macro_event_id: macro_event_id || "",
        collection,
        type,
        updated_at,
        from_children,
        op_sequence,
        op_status,
        raw_op_id,
        ref_article,
        ref_client,
        max_date_of,
        quantity_produced,
        op_name,
        famille,
        op_order: op_order === null || op_order === undefined ? 999 : +(op_order || 0),
        tags: typeof tags === "string" ? [tags] : tags,
        fault,
        quantite_op_2,
        quantite_of,
        fast_track,
        order_number,
        customer,
        is_new,
        data_source,
        status_done_at,
        status_ongoing_at,
        status_available_at,
        status_updated_at,
        prod_updated_at,
        erp_date,
        prod_target: !!prod_target,
        is_late,
        internal_extra_infos,
        extra_infos,
        partition_key,
        target_ref_article,
        import_type,
        quantite_of_besoin,
        quantite_cumul_rebut,
        quantite_op_rebut,
        quantite_of_disponible,
        op_tags: typeof op_tags === "string" ? [op_tags] : op_tags,
        op_fast_track,
    };
    Object.keys(cleanData).forEach((key) => {
        if (cleanData[key] === undefined)
            cleanData[key] = null; //This is to avoid keys being deleted in a JSON.stringify() or when sent to server
    });
    return cleanData;
};
const dailyLoadKeys = Object.keys(prepareDailyLoadData({}));
const dailyLoadPrimaryKeysFields = [
    "client_id",
    "simulation_id",
    "secteur_id",
    "op_id",
    "day_date",
    "import_type",
    "macro_event_id",
    "partition_key",
];
const updatableDailyLoadKeys = dailyLoadKeys.filter((key) => !dailyLoadPrimaryKeysFields.includes(key));
const loadInternalExtraInfosKeys = [
    ...rawLoadInternalExtraInfosKeys,
    "of_delta",
    "first_active_op_theoric_date",
    "first_active_op_planned_date",
    "all_ops_done",
    "real_date",
    "real_date_delta",
    "initial_secteur_id",
    "is_smoothed",
    "smooth_idx",
    "smooth_dates",
    "cross_simulation_id",
    "is_delay",
    "production_load_strategy",
    "from_stock",
    "rescheduling_id",
    "parent_of_id",
    "first_day_duration",
    "max_daily_capa",
    "wip_order",
    "batch_id",
    "batch_name",
    "is_day_date_modified",
];
const dailyLoadKeysByType = {
    number: [
        "load",
        "op_sequence",
        "quantity_produced",
        "op_order",
        "quantite_op_2",
        "quantite_of",
    ],
    boolean: ["from_children", "is_new", "prod_target", "is_late"],
};
//This is a simple implementation of the type of LoadEvent, just making sure that we always have the same object, and that number are not strings for ex
const prepareLoadEventData = (rawData) => {
    const rawPreparedData = _.omit(prepareDailyLoadData(rawData), [
        "partition_key",
        "macro_event_id",
        "from_children",
        "data_source",
        "import_type",
    ]);
    const { event_id, event_type, event_updated_at, event_level, is_scheduling_simulation, event_extra_infos, 
    //we must take the raw values of numbers to avoid parsing that'll make it look like an update
    load, op_order, op_sequence, quantity_produced, quantite_op_2, quantite_of, quantite_of_besoin, quantite_cumul_rebut, quantite_op_rebut, quantite_of_disponible, prod_target, is_late, is_new, extra_infos, } = rawData;
    const { client_id, import_id, simulation_id, secteur_id, op_id, of_id, day_date, collection, type, updated_at, op_status, raw_op_id, ref_article, ref_client, max_date_of, op_name, famille, tags, op_tags, fault, fast_track, op_fast_track, order_number, customer, status_done_at, status_ongoing_at, status_available_at, status_updated_at, prod_updated_at, erp_date, } = rawPreparedData;
    let { internal_extra_infos } = rawData;
    //we inject the keys in internal_extra_infos if relevant
    loadInternalExtraInfosKeys.forEach((key) => {
        if ([null, undefined, ""].includes(rawData[key]))
            return;
        if (!internal_extra_infos)
            internal_extra_infos = {};
        internal_extra_infos[key] = rawData[key];
    });
    const cleanData = {
        client_id,
        import_id: import_id || "",
        simulation_id: simulation_id || "",
        event_id,
        event_type: event_type || "UPDATE",
        event_updated_at,
        event_level: event_level || "OP",
        is_scheduling_simulation,
        secteur_id: secteur_id,
        op_id: op_id || "",
        of_id: of_id || "",
        day_date: day_date || "",
        load: [null, undefined, ""].includes(load)
            ? undefined
            : +(load || 0),
        collection,
        type,
        updated_at,
        op_sequence: [null, undefined, ""].includes(op_sequence)
            ? undefined
            : +(op_sequence || 0),
        op_status,
        raw_op_id,
        ref_article,
        ref_client,
        max_date_of,
        quantity_produced: [null, undefined, ""].includes(quantity_produced)
            ? undefined
            : +(quantity_produced || 0),
        op_name,
        famille,
        op_order: [null, undefined, ""].includes(op_order)
            ? undefined
            : +(op_order || 0),
        tags,
        op_tags,
        fault,
        quantite_op_2: [null, undefined, ""].includes(quantite_op_2)
            ? undefined
            : +(quantite_op_2 || 0),
        quantite_of: [null, undefined, ""].includes(quantite_of)
            ? undefined
            : +(quantite_of || 0),
        quantite_of_besoin: [null, undefined, ""].includes(quantite_of_besoin)
            ? undefined
            : +(quantite_of_besoin || 0),
        quantite_op_rebut: [null, undefined, ""].includes(quantite_op_rebut)
            ? undefined
            : +(quantite_op_rebut || 0),
        quantite_cumul_rebut: [null, undefined, ""].includes(quantite_cumul_rebut)
            ? undefined
            : +(quantite_cumul_rebut || 0),
        quantite_of_disponible: [null, undefined, ""].includes(quantite_of_disponible)
            ? null
            : +(quantite_of_disponible || 0),
        fast_track,
        op_fast_track,
        order_number,
        customer,
        is_new: [null, undefined, ""].includes(is_new)
            ? undefined
            : !!is_new,
        status_done_at,
        status_ongoing_at,
        status_available_at,
        status_updated_at,
        prod_updated_at,
        erp_date,
        prod_target: [null, undefined, ""].includes(prod_target)
            ? undefined
            : !!prod_target,
        is_late: [null, undefined, ""].includes(is_late)
            ? undefined
            : !!is_late,
        internal_extra_infos,
        extra_infos,
        event_extra_infos,
    };
    Object.keys(cleanData).forEach((key) => {
        if (cleanData[key] === undefined)
            cleanData[key] = null; //This is to avoid keys being deleted in a JSON.stringify() or when sent to server
    });
    return cleanData;
};
const loadEventKeys = Object.keys(prepareLoadEventData({}));
const loadEventPrimaryKeysFields = [
    "client_id",
    "import_id",
    "simulation_id",
    "event_id",
    "of_id",
    "op_id",
    "day_date",
    "is_scheduling_simulation",
];
const updatableLoadEventKeys = loadEventKeys.filter((key) => !loadEventPrimaryKeysFields.includes(key));
const booleanLoadEventKeys = [
    "is_parsed",
    "is_scheduling_simulation",
    "prod_target",
    "is_late",
    "is_new",
    "from_children",
];
const prepareLoadEventRowData = (rawData) => {
    const { client_id, import_id, simulation_id, op_id, of_id, event_id, event_type, event_updated_at, event_level, is_scheduling_simulation, event_field, event_value, event_extra_infos, } = rawData;
    if (!canHaveEmptyStringValue(event_field, event_value))
        return;
    if (!dailyLoadKeys.includes(event_field))
        return;
    if ([...loadEventRowKeys, "updated_at", "event_extra_infos"].includes(event_field))
        return;
    const cleanData = {
        client_id,
        import_id: import_id || "",
        simulation_id: simulation_id || "",
        event_id: event_id || "",
        event_type: event_type || "UPDATE",
        event_updated_at,
        event_level: event_level || "OP",
        is_scheduling_simulation,
        op_id: op_id || "",
        of_id: of_id || "",
        event_field,
        event_value,
        event_extra_infos,
    };
    return cleanData;
};
const baseEventRowKeys = [
    "client_id",
    "simulation_id",
    "event_id",
    "event_field",
    "event_value",
    "event_extra_infos",
];
const loadEventRowKeys = [
    ...baseEventRowKeys,
    "import_id",
    "event_type",
    "event_updated_at",
    "event_level",
    "is_scheduling_simulation",
    "op_id",
    "of_id",
];
const stockEventsRowsKeys = [
    ...baseEventRowKeys,
    "event_updated_at",
    "impacted_scope",
    "ref_article",
    "secteur_id",
    "op_sequence",
    "start_date",
    "end_date",
];
const loadOfsKeys = [
    "client_id",
    "simulation_id",
    "of_id",
    "nb_ops_total",
    "nb_ops_done",
    "first_available_op_sequence",
    "last_op_planned_date",
    "last_op_erp_date",
    "all_ops_done",
    "first_active_op_theoric_date",
    "first_active_op_planned_date",
    "last_done_op_delay",
    "estimated_end_date",
    "of_delta",
    "partition_key",
    "quantite_of_rebut",
];
const updatableLoadOfsKeys = loadOfsKeys.filter((key) => !["client_id", "simulation_id", "of_id"].includes(key));
/**
 * Checks if a loadEventRow/firestoreDoc field can have an empty string value
 * @param {string} field - The name of the field.
 * @param {any} value - The value of the field.
 * @returns {boolean} True if the field can have an empty string value, false otherwise.
 */
function canHaveEmptyStringValue(field, value) {
    const emptyStringFields = [
        "fast_track",
        "op_fast_track",
        "new_fault",
        "fault",
    ];
    if (![undefined, null, ""].includes(value))
        return true;
    if (emptyStringFields.includes(field) &&
        value !== undefined &&
        value !== null)
        return true;
    return false;
}
// New keys added at the end of all load tables (drln, dln, drlc, drlcff, dlc)
//
// Makes it easier to exclude them when spreading columns from daily_load_new for example, as
// concatenated tables have intermediary data that needs to be dealt with first
const addedLoadTableKeys = [
    "target_ref_article",
    "import_type",
    "quantite_of_besoin",
    "quantite_cumul_rebut",
    "quantite_op_rebut",
    "quantite_of_disponible",
    "op_tags",
    "op_fast_track",
];
const addedRawLoadTableKeys = [
    ...addedLoadTableKeys.filter((k) => !["quantite_of_disponible"].includes(k)),
    "quantite_of_disponible",
];
const dailyRawLoadConcatenatedKeys = [
    ...dailyRawLoadKeys.filter(
    // Those specific keys have been added at the very end of drlc, which creates a disparity between drln and drlc
    (key) => !addedRawLoadTableKeys.includes(key)),
    "first_inserted_at",
    "last_inserted_at",
    "first_erp_date",
    ...addedRawLoadTableKeys,
];
const dailyRawLoadConcatenatedFromFiles = [
    ...dailyRawLoadConcatenatedKeys.filter(
    // Those specific keys have been added at the very end of drlcff, which creates a disparity between drlc and drlcff
    (key) => !addedRawLoadTableKeys.includes(key)),
    "generated_at",
    ...addedRawLoadTableKeys,
];
const dailyLoadConcatenatedKeys = [
    ...dailyLoadKeys.filter(
    // Those specific keys have been added at the very end of dlc, which creates a disparity between dln and dlc
    (key) => !addedLoadTableKeys.includes(key)),
    "first_inserted_at",
    "last_inserted_at",
    "first_erp_date",
    "all_secteur_ids",
    ...addedLoadTableKeys,
];
const basePdpLoadEventKeys = [
    "client_id",
    "simulation_id",
    "event_id",
    "event_type",
    "event_status",
];
const updatablePdpLoadEventKeys = [
    "event_name",
    "start_date",
    "end_date",
    "load_name",
    "assignations",
    "op_ids",
    "initial_quantity",
    "quantity",
    "initial_sector_id",
    "new_sector_id",
    "should_sync_ops",
];

const levelCorresp = [
    { type: "site", level: 0, collection: "sites", displayName: "Site" },
    { type: "factory", level: 1, collection: "factories", displayName: "UAP" },
    { type: "boucle", level: 2, collection: "boucles", displayName: "Boucle" },
    {
        type: "sous_boucle",
        level: 3,
        collection: "sous_boucles",
        displayName: "Sous boucle",
    },
    {
        type: "sur_ilot",
        level: 4,
        collection: "sur_ilots",
        displayName: "Sur îlot",
    },
    { type: "ilot", level: 5, collection: "ilots", displayName: "Îlot" },
    {
        type: "operation",
        level: 6,
        collection: "operations",
        displayName: "Poste de charge",
    },
    { type: "machine", level: 7, collection: "machines", displayName: "Machine" },
];
const reverseLevelCorresp = levelCorresp.slice().reverse();
const levelCorrespWithGroup = [
    {
        type: "group",
        level: -1,
        collection: "groups",
        displayName: "Groupe",
    },
    ...levelCorresp,
];
const reverseLevelCorrespWithGroup = levelCorrespWithGroup.slice().reverse();
const merca_levels = [
    {
        type: "site",
        level: 0,
        collection: "sites",
        displayName: "Site",
        data_type: "object",
    },
    {
        type: "factory",
        level: 1,
        collection: "factories",
        displayName: "UAP",
        data_type: "object",
    },
    {
        type: "team",
        level: 2,
        collection: "teams",
        displayName: "Equipe",
        data_type: "array",
    },
    {
        type: "position",
        level: 3,
        collection: "positions",
        displayName: "Position",
        data_type: "object",
    },
    {
        type: "subposition",
        level: 4,
        collection: "subpositions",
        displayName: "Sous-position",
        data_type: "object",
    },
];
const getValueInOpOrExtraInfos = (operation, field) => {
    var _a, _b;
    return ((_b = (_a = _.get(operation, field)) !== null && _a !== void 0 ? _a : _.get(operation, ["internal_extra_infos", field])) !== null && _b !== void 0 ? _b : _.get(operation, ["extra_infos", field]));
};
const evaluateCapa = function (base_formula, serie, divide_percent = false, event_days = 1, total_days = 1, label = null, calculated = {}, returnError = false) {
    //TODO: change label to string only, format YYY-MM-DD
    let capa = 0;
    if (!(base_formula === null || base_formula === void 0 ? void 0 : base_formula.length))
        return capa;
    if (![undefined, null].includes(serie.capa_validee))
        return (serie.capa_validee * event_days) / (total_days * total_days);
    const formula = base_formula.reduce((acc, curr) => {
        var _a;
        let model = curr.model;
        if (model === "children") {
            const childrenValues = [];
            (serie.children || []).forEach((child) => {
                if (!child)
                    return;
                childrenValues.push(evaluateCapa(child.formula, child, false, event_days, total_days, label, calculated));
            });
            // @ts-ignore
            return acc + _[((_a = serie.aggregation_method) === null || _a === void 0 ? void 0 : _a.value) || "sum"](childrenValues);
        }
        if (curr.type === "child") {
            let targetSerie = calculated[curr.model];
            if (curr.model === "capa")
                targetSerie = serie;
            const child = findRecursively(targetSerie, curr.secteur_id) || {};
            const currMaille = moment(label).format("YYYY-MM-DD");
            const model_maille = (child.data || []).find((x) => moment(x.label).format("YYYY-MM-DD") === currMaille);
            model = (model_maille === null || model_maille === void 0 ? void 0 : model_maille.value) || 0;
            if (curr.model === "capa" && model)
                model = model / total_days;
        }
        else if (curr.inputId) {
            model = serie[model];
            if ([undefined, null].includes(model)) {
                model = curr.default_value;
                if (!divide_percent && model && curr.inputId === "percent")
                    model = model / 100;
            }
            if (curr.type === "dropdown" && typeof model === "object")
                model = _.get(model, ["values", 0]);
            if (divide_percent && model && curr.inputId === "percent")
                model = model / 100;
        }
        if (model === "x" && curr.type === "operation")
            model = "*";
        return acc + model;
    }, "");
    try {
        capa = new Function(`return ${formula}`)() || 0;
        return capa;
    }
    catch (e) {
        if (returnError)
            return false;
        return 0;
    }
};
const findRecursively = (serie, id) => {
    if (!serie)
        return false;
    if ([serie.secteur_id, serie.id].includes(id))
        return serie;
    let result;
    (serie.children || []).forEach((child) => {
        if (!child)
            return false;
        return (result = result || findRecursively(child, id));
    });
    return result;
};
const asyncForEach = function (array, callback) {
    return __awaiter(this, void 0, void 0, function* () {
        for (let index = 0; index < (array || []).length; index++)
            yield callback(array[index], index, array);
    });
};
const parseMaille = function (maille) {
    if (maille === "day") {
        return {
            graphFormat: "DD/MM",
            momentFormat: "DD MM YYYY",
            txtFormat: "DD/MM",
            yearlyFormat: "DDD",
        };
    }
    else if (maille === "week") {
        return {
            graphFormat: "DD/MM",
            momentFormat: "WW YYYY",
            txtFormat: "DD MMM YYYY",
            yearlyFormat: "w",
        };
    }
    else if (maille === "quarter") {
        return {
            graphFormat: "Q YYYY",
            momentFormat: "Q YYYY",
            txtFormat: "[Q]Q YYYY",
            yearlyFormat: "Q",
        };
    }
    else if (maille === "year") {
        return {
            graphFormat: "YYYY",
            momentFormat: "YYYY",
            txtFormat: "YYYY",
            yearlyFormat: "Y",
        };
    }
    else {
        return {
            graphFormat: "MMMM",
            momentFormat: "MM YYYY",
            txtFormat: "MMM YYYY",
            yearlyFormat: "M",
        };
    }
};
const periodeToText = function (periode) {
    let text = new Date().getFullYear() + "-01-01";
    if (typeof periode === "string")
        text = periode;
    else if (typeof periode === "object" && (periode === null || periode === void 0 ? void 0 : periode.length)) {
        const startDate = _.min(periode);
        const isMonth = ((startDate === null || startDate === void 0 ? void 0 : startDate.match(/-/g)) || []).length === 1;
        let endDate = _.max(periode);
        endDate = isMonth ? moment(endDate).endOf("month") : endDate;
        if (startDate !== endDate) {
            const defaultFormat = "D MMM YYYY";
            const [dStartDate, mStartDate, yStartDate] = moment(startDate)
                .format(defaultFormat)
                .split(" ");
            const [dEndDate, mEndDate, yEndDate] = moment(endDate)
                .format(defaultFormat)
                .split(" ");
            const isSameMonth = mStartDate === mEndDate;
            const isSameYear = yStartDate === yEndDate;
            const isCurrentYear = isSameYear && moment(startDate).year() === moment().year();
            const displayedStartDate = isSameYear
                ? isSameMonth
                    ? dStartDate
                    : `${dStartDate} ${mStartDate}`
                : `${dStartDate} ${mStartDate} ${yStartDate}`;
            const displayedEndDate = isCurrentYear
                ? `${dEndDate} ${mEndDate}`
                : `${dEndDate} ${mEndDate} ${yEndDate}`;
            return `${displayedStartDate} - ${displayedEndDate}`;
        }
        else {
            const format = (isMonth ? "" : "DD ") + "MMM YYYY";
            text = moment(startDate).format(format);
        }
    }
    return text;
};
//TODO move to webapp
const computeInitials = function (operator) {
    if (!operator)
        return "";
    else if (operator.name) {
        return operator.name
            .split(" ")
            .map((x) => x.charAt(0))
            .join(".");
    }
    else if (operator.first_name || operator.last_name) {
        return [operator.first_name, operator.last_name]
            .filter((x) => !!x)
            .map((x) => x.charAt(0))
            .join(".");
    }
    else
        return "";
};
//TODO move to webapp
const computePlaceholderColor = function (operator, variables) {
    if (!operator)
        return "";
    const name = operator.name || operator.first_name || operator.last_name;
    if (!name)
        return "";
    const firstLetter = name.charCodeAt(0);
    const keys = Object.keys(variables).filter((x) => x.includes("Light2") || x.includes("Dark1"));
    const position = firstLetter % keys.length;
    const contrast = position % 2 ? position - 1 : position + 1;
    const backColor = keys[position > contrast ? position : contrast];
    const txtColor = keys[position > contrast ? contrast : position];
    return `${backColor} text-${txtColor}`;
};
const getFirstAssignation = function (item) {
    const first_assignation = _.get(item, ["assignations", 0], {});
    let parent_text = "";
    if (!item.is_mercateam) {
        const parentLevel = _.findLast(levelCorresp, (level) => first_assignation[level.type + "_id"] !== undefined);
        const parent = first_assignation[(parentLevel === null || parentLevel === void 0 ? void 0 : parentLevel.type) + "_name"];
        parent_text = parent ? parent + " > " : "";
    }
    return parent_text + (first_assignation.name || "");
};
const getOperatorsKeys = function (operator = {}, parametres_msd = {}) {
    const { id, name, add, assignment_percentage } = operator;
    const newOperator = {
        id,
        name,
        add,
        assignment_percentage,
    };
    Object.keys(newOperator).forEach((key) => {
        if (newOperator[key] === undefined)
            delete newOperator[key];
    });
    (parametres_msd.fields || []).forEach((field) => {
        if (field.in_formula && ![undefined, null].includes(operator[field.model]))
            newOperator[field.model] = operator[field.model];
    });
    return newOperator;
};
const getSectorKeys = function (sector) {
    const cleaned = {};
    [...levelCorresp, { type: "parent" }].forEach((level) => {
        if (sector[level.type + "_id"]) {
            cleaned[level.type + "_id"] = sector[level.type + "_id"];
            cleaned[level.type + "_name"] = sector[level.type + "_name"] || null;
        }
    });
    cleaned.collection = sector.collection || null;
    cleaned.level = sector.level || null;
    cleaned.type = sector.type || null;
    cleaned.id = sector.id || sector.secteur_id || null;
    cleaned.name = sector.name || sector.secteur_name || null;
    if (sector.operateur_id) {
        cleaned.operateur_id = sector.operateur_id;
        cleaned.operateur_name = sector.operateur_name || null;
    }
    else {
        cleaned.secteur_id = sector.secteur_id || sector.id || null;
        cleaned.secteur_name = sector.secteur_name || sector.name || null;
    }
    if (sector.machine_tag_id) {
        cleaned.machine_tag_id = sector.machine_tag_id;
        cleaned.machine_tag_name = sector.machine_tag_name || null;
    }
    return cleaned;
};
const getSectorCalendar = function (calendar, sector) {
    if (!sector)
        return calendar;
    const secteur_id = (sector === null || sector === void 0 ? void 0 : sector.secteur_id) || (sector === null || sector === void 0 ? void 0 : sector.id);
    return (calendar || []).map((x) => x.is_child_cal === undefined
        ? x
        : Object.assign(Object.assign({}, x), { is_child_cal: ![secteur_id, sector[x.type + "_id"]].includes(x.secteur_id) }));
};
const simplifySector = function (secteur) {
    // TODO refactor this using _.omit() or similar
    /* eslint-disable */
    const { machine_tags, operateurs, client_id: parent_client_id = null, operateur, operators, operator, machines, machine } = secteur, rest = __rest(secteur, ["machine_tags", "operateurs", "client_id", "operateur", "operators", "operator", "machines", "machine"]);
    /* eslint-enable */
    const simpleActivePerim = Object.assign(Object.assign(Object.assign(Object.assign({}, rest), { client_id: parent_client_id }), (machine_tags && {
        machine_tags: machine_tags.map((tag) => {
            const { id, name, site_id, type, client_id = parent_client_id } = tag;
            return Object.assign(Object.assign(Object.assign({ id,
                client_id }, (name && { name })), (site_id && { site_id })), (type && { type }));
        }),
    })), (operateurs && {
        operateurs: operateurs.map((tag) => {
            const { id, name, site_id, type, client_id = parent_client_id } = tag;
            return Object.assign(Object.assign(Object.assign({ id,
                client_id }, (name && { name })), (site_id && { site_id })), (type && { type }));
        }),
    }));
    return simpleActivePerim;
};
const computeImpact = function (event, daily = false, reduce = false, returnRaw = false) {
    let value;
    if (!event || !event.impact)
        value = 0;
    else if (typeof event.impact === "object") {
        if (daily)
            value = event.impact.daily;
        else
            value = event.impact.value;
    }
    else
        value = event.impact;
    if (!value || isNaN(value) || !isFinite(value))
        value = 0;
    value = Math.round(value * 100) / 100;
    const unit = (event === null || event === void 0 ? void 0 : event.unit) || "";
    try {
        if (returnRaw)
            return value;
        return `${numeral(value).format(reduce ? "0.0a" : "0,0")} ${unit}`;
    }
    catch (e) {
        // loggerHelper.log("numeral error : ", value, reduce ? "0.0a" : "0,0", unit);
        return `${value} ${unit}`;
    }
};
const computeChipColor = function (event) {
    var _a;
    return ((_a = event.impact) === null || _a === void 0 ? void 0 : _a.value) > 0 ? "newGreenRegular" : "newPinkDark1";
};
const bigqueryOfFields = [
    "division",
    "etablissement",
    "num_op",
    "statut_op",
    "code_article",
    "reference",
    "priorite",
    "pdc",
    "par_combien",
    "designation_op",
    "tps_fabrication",
    "type_statut",
    "production_factor_group",
    "type_structure",
    "type_of",
    "of_ou_pof",
    "macrogamme",
    "num_boucle",
    "uap",
    "name",
    "quantite",
    "datelancement",
    "ilot",
    "retard",
    "retard_poids",
    "eh_ou_kg",
    "poids",
    "retard_poids_ligne",
    "retard_ligne",
    "of_type",
    "num_of",
    "id",
    "poids_net",
    "poids_brut",
    "num_of_client",
    "reference_article",
    "extra_infos",
    "unite",
    "ref_article",
    "order_number",
    "customer",
    "datesortie",
    "op_id",
    "op_name",
    "op_sequence",
    "op_status",
    "of_id",
    "day_time",
    "csv_row_number",
];
const bigqueryCommandesFields = [
    "ncommande",
    "nligne",
    "statut_commande",
    "type_commande",
    "ncommande_client",
    "nligne_commande_client",
    "nclient",
    "nom_client",
    "date_livraison",
    "temps_restant_commande_sans_le_wk",
    "code_article",
    "reference_article",
    "code_article_composant_1",
    "code_article_composant_2",
    "ligne_produit",
    "ligne_produit_qte_consommee",
    "qte_commandee",
    "qte_commandee_unique",
    "qte_stock_pf",
    "qte_stock_cp_1",
    "qte_stock_cp_2",
    "qte_rest_a_couvrir",
    "qte_consommee",
    "provenance_qte",
    "cp_lie_a_un_of_pf",
    "montant",
    "devise",
    "nof",
    "code_article_of",
    "qte_of",
    "qte_restante_of",
    "qte_of_unique",
    "priorite",
    "poids_of_unique",
    "protection",
    "nop",
    "pdc",
    "designation_op",
    "statut_op",
    "temps_attente_op_sans_le_wk",
    "date_fin_of_prevue",
    "temps_restant_fab_sans_le_wk",
    "temps_restant_fab_calendaire",
    "ratio_critique",
    "identifiant",
    "ilot",
    "is_late",
    "is_prev",
    "quantite_stock",
    "quantite_restante",
    "montant_stock",
    "montant_restant",
];
const makeOPsUnique = (raw_data, id_name = "of_id") => {
    let data = [...(raw_data || [])];
    //We double check that all the ofs are unique
    const duplicates = _.pickBy(_.groupBy(data, (o) => o.op_id || o.of_id || o.id), (subRows) => (subRows === null || subRows === void 0 ? void 0 : subRows.length) > 1);
    const duplicateKeys = _.keys(duplicates) || [];
    if (duplicateKeys === null || duplicateKeys === void 0 ? void 0 : duplicateKeys.length) {
        data = data.map((row) => {
            const id = row.op_id || row.of_id || row.id || "";
            if (!duplicateKeys.includes(id))
                return row;
            else {
                return Object.assign(Object.assign({}, (duplicates[id].shift() || row)), { [id_name]: id + "_" + duplicates[id].length });
            }
        });
    }
    return data;
};
// TODO move to webapp
const getAllChildrenFrom = (baseSector) => {
    var _a;
    if (!((_a = baseSector.children) === null || _a === void 0 ? void 0 : _a.length))
        return [];
    return baseSector.children.reduce((acc, sector) => {
        const children = getAllChildrenFrom(sector);
        if (!Array.isArray(children)) {
            acc.push(children);
            return acc;
        }
        else
            return [...acc, sector, ...getAllChildrenFrom(sector)];
    }, []);
};
//scheduling utils but placed here to avoid circular imports in sharedSchedulingUtils
const opStatusList = ["pof", "pending", "available", "on-going", "done"];
const getMaxStatus = (statuses) => {
    const maxIdxMatched = _.findLastIndex(opStatusList, (s) => statuses.includes(s));
    if (maxIdxMatched === -1)
        return;
    return opStatusList[maxIdxMatched];
};
const getMaxStatusFromConcatenatedStatuses = (statuses) => {
    if (!(statuses === null || statuses === void 0 ? void 0 : statuses.length))
        return;
    const statusArr = statuses.split(",");
    const maxIdxMatched = _.findLastIndex(opStatusList, (s) => statusArr.includes(s));
    if (maxIdxMatched === -1)
        return;
    return opStatusList[maxIdxMatched];
};
const pgOpsMapFn = (results, params) => {
    const { of_ordering = false, keepOpsDone = false, removeSmoothedDuplicates = false, groupBySequence = false, keepExtraInfos = false, schedulingTagsIdsSet, } = params || {};
    const ops = (results || []).map((op) => {
        var _a, _b;
        const { op_id: tempOpId, id, //legacy, should be removed for op_id
        op_name, name, //legacy, should be removed for op_name
        macro_event_id: firestore_id, of_id, internal_extra_infos, extra_infos = {}, tags = [], op_tags = [] } = op, rest = __rest(op, ["op_id", "id", "op_name", "name", "macro_event_id", "of_id", "internal_extra_infos", "extra_infos", "tags", "op_tags"]);
        const _c = internal_extra_infos || {}, { is_delay } = _c, ieirest = __rest(_c, ["is_delay"]);
        const op_id = tempOpId !== null && tempOpId !== void 0 ? tempOpId : id;
        const quantite = (_a = op.quantite) !== null && _a !== void 0 ? _a : op.load;
        const coalescedOp = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, ieirest), rest), { quantite_of: (_b = getValueInOpOrExtraInfos(op, "quantite_of")) !== null && _b !== void 0 ? _b : quantite, op_id,
            op_name, of_name: name !== null && name !== void 0 ? name : op_name, of_id, quantite_op: quantite, quantite,
            firestore_id,
            tags,
            op_tags }), (is_delay && { delay: is_delay })), (keepExtraInfos && { extra_infos }));
        // removes tags that are maybe stored in postGres but not in firestore (OPL-8647)
        if (schedulingTagsIdsSet) {
            coalescedOp.tags = tags.filter((tag) => schedulingTagsIdsSet.has(tag));
            coalescedOp.op_tags = op_tags.filter((op_tags) => schedulingTagsIdsSet.has(op_tags));
        }
        Object.keys(coalescedOp).forEach((key) => {
            if (coalescedOp[key] === "false")
                coalescedOp[key] = false;
            else if (coalescedOp[key] === "0" && key !== "op_order")
                coalescedOp[key] = false;
        });
        Object.keys(extra_infos || {}).forEach((key) => {
            if ((extra_infos === null || extra_infos === void 0 ? void 0 : extra_infos[key]) === "false")
                extra_infos[key] = false;
        });
        return Object.assign(Object.assign({}, (extra_infos || {})), coalescedOp);
    });
    let filteredOps = ops.filter((x) => x.op_id &&
        (keepOpsDone || x.op_status !== "done") &&
        (!removeSmoothedDuplicates || !x.smooth_idx));
    //regroup OPs with the same sequence id
    if (groupBySequence) {
        const opsGroupedBySequence = _.groupBy(filteredOps, "op_sequence");
        filteredOps = Object.keys(opsGroupedBySequence).map((sequenceId) => {
            const ops = opsGroupedBySequence[sequenceId];
            if (ops.length === 1)
                return ops[0];
            const nbDistinctSectorIds = _.uniqBy(ops, "secteur_id").length;
            const lastOp = _.maxBy(ops, "op_id"); //by taking the max we ensure that if one is passed to "done", all previous ones will too
            return Object.assign(Object.assign({}, lastOp), { ops_w_same_sequence: nbDistinctSectorIds - 1 });
        });
    }
    const pg_ops = _.orderBy(filteredOps, [
        (o) => {
            var _a;
            if (of_ordering) {
                return o.op_sequence === null || o.op_sequence === undefined
                    ? (_a = o.new_date) !== null && _a !== void 0 ? _a : o.day_date
                    : +o.op_sequence;
            }
            if (o.new_order !== undefined)
                return +(o.new_order || 0);
            return +(o.op_order || 0);
        },
        (o) => { var _a; return (_a = o.new_date) !== null && _a !== void 0 ? _a : o.day_date; },
        "op_id",
    ]);
    const groupedOpsByUniqueId = _.groupBy(pg_ops, (o) => `${o.op_id}_${o.smooth_idx}`);
    const deduplicatedOps = Object.values(groupedOpsByUniqueId).reduce((acc, ops) => {
        const [op] = ops;
        if (ops.length === 1)
            return [...acc, op];
        const sumLoadOp = _.sumBy(ops, (o) => +(o.load || 0));
        const sumQteOp = _.sumBy(ops, (o) => +(o.quantite || 0));
        return [...acc, Object.assign(Object.assign({}, op), { load: sumLoadOp, quantite: sumQteOp })];
    }, []);
    return deduplicatedOps;
};
/**
 * Contains merged/coalesced values for scheduling display
 * Essential to filters and sort logics
 * Alters : fast_track, tags
 * @param op Initial operation
 * @returns operation with merged parameters
 */
const getMergedSchedulingOp = (op) => {
    const { fast_track, op_fast_track, tags, op_tags } = op, rest = __rest(op, ["fast_track", "op_fast_track", "tags", "op_tags"]);
    return Object.assign(Object.assign({}, rest), { fast_track: op_fast_track || fast_track || undefined, tags: [...new Set([...(op_tags !== null && op_tags !== void 0 ? op_tags : []), ...(tags !== null && tags !== void 0 ? tags : [])])] });
};
const buildRecursivelySectorTree = function (serie, perimetres) {
    if (!serie)
        return serie;
    const { collection, level, type, id, name, order = null, unite = null, mercateam_id, movex_id, } = serie;
    const cleaned = Object.assign({ collection,
        level,
        type,
        id, secteur_id: id, name, secteur_name: name, order,
        unite, has_movex_id: !!movex_id }, (mercateam_id && { mercateam_id }));
    levelCorresp.forEach((sublevel) => {
        if (![null, undefined].includes(serie[sublevel.type + "_id"])) {
            cleaned[sublevel.type + "_id"] = serie[sublevel.type + "_id"];
            cleaned[sublevel.type + "_name"] = serie[sublevel.type + "_name"];
        }
    });
    const children = getSortedPerimeters((Array.isArray(serie.children) ? serie.children : [])
        .map((x) => {
        if (!(x === null || x === void 0 ? void 0 : x.collection) || !(x === null || x === void 0 ? void 0 : x.id))
            return;
        const match = (perimetres[x.collection] || []).find((y) => x.id === y.id);
        if (!match)
            return;
        if (["removed", "deleted"].includes(match.status))
            return;
        return buildRecursivelySectorTree(match, perimetres);
    })
        .filter(Boolean));
    return Object.assign(Object.assign({}, cleaned), (children.length && { children }));
};
/**
 * returns the total of operators on a given @period
 */
const getPeopleOrgaFromPeriod = (period /* OplitOperatorPeriod */) => {
    if (!period.total_days)
        return null;
    if (!(period === null || period === void 0 ? void 0 : period.daily_nb_machine))
        return 0;
    if (!(period === null || period === void 0 ? void 0 : period.daily_nb_people))
        return 0;
    if (!(period === null || period === void 0 ? void 0 : period.peoples))
        return 0;
    return period.assigned_orga * period.total_days;
};
/**
 * returns the total of operators on a given @periodArray
 */
const getPeopleOrgaFromPeriodArray = (periodArray /* OplitOperatorPeriod[] */) => {
    if (!periodArray.length)
        return 0;
    // only consider days that are workdays (otherwise `total_days` equals 0)
    const periodWithDays = periodArray.filter(({ total_days }) => total_days > 0);
    if (!periodWithDays.length)
        return 0;
    const nbDays = _.sum(periodWithDays.map((x) => x.total_days));
    const sumOrga = _.sum(periodWithDays.map(getPeopleOrgaFromPeriod));
    return _.round(sumOrga / nbDays, 1);
};
/*
 * from @sector returns an object with keys representing entities this sector is a part of
 */
const getLevelCorrespFieldsForSector = (sector) => {
    const levelCorrespItem = {};
    levelCorresp.forEach((level) => {
        if (sector[`${level.type}_id`]) {
            levelCorrespItem[`${level.type}_id`] = sector[`${level.type}_id`];
            levelCorrespItem[`${level.type}_name`] =
                sector[`${level.type}_name`] || null;
        }
    });
    return levelCorrespItem;
};
const toSafeString = (str) => {
    return (str || "") + ""; //transforms all inputs to string. Be aware that (""+undefined = "undefined")
};
function isSafeSectorNameMatching(currentFactoryName, factoryNameAgainst) {
    if (!currentFactoryName || !factoryNameAgainst)
        return false;
    return toSafeString(currentFactoryName)
        .toLowerCase()
        .includes(factoryNameAgainst.toLowerCase());
}
const searchSubstring = (mainString, searchString) => {
    const main = _.deburr(toSafeString(mainString)) // change to string and remove all unsafe characters
        .toLowerCase();
    const search = _.deburr(toSafeString(searchString)).toLowerCase();
    return main.includes(search);
};
const searchSanitizedSubstring = (mainString, searchString) => {
    const sanitizedMainString = getReadableImportParsingRuleValue(mainString);
    const sanitizedSearchString = getReadableImportParsingRuleValue(searchString);
    return searchSubstring(sanitizedMainString, sanitizedSearchString);
};
/**
 * maps the objects of an array with the `order` key, representing their index in the current array + 1
 */
function mapArrayWithOrder(array) {
    if (!(array === null || array === void 0 ? void 0 : array.length))
        return [];
    return Array.from(array, (item, index) => (Object.assign(Object.assign({}, item), { order: ++index })));
}
//check if a given sector has the planning activated
const hasSectorAutoOrga = (params) => {
    const { planning_sectors, secteur_id, secteur_type } = params;
    if (!secteur_id)
        return false;
    const has_planning = (planning_sectors || []).some((sector) => sector.secteur_id === secteur_id ||
        sector[`${secteur_type}_id`] === secteur_id); //either the sector has the planning, or any of its children
    return has_planning;
};
/**
 * generic sorting function for perimeters within the app
 */
function getSortedPerimeters(perimetersArray) {
    return _.sortBy(perimetersArray, [
        // we need to fallback for undefined/null values which represent unconfigured perimeters : we push at the end of the list
        (o) => { var _a; return ((_a = o === null || o === void 0 ? void 0 : o.order) !== null && _a !== void 0 ? _a : Infinity) / 1; },
        "name",
    ]);
}
function getStandardDailyLoadsFromSpreadOnes(spreadDailyLoads) {
    return spreadDailyLoads.map((spreadDailyLoad) => {
        const keys = Object.keys(spreadDailyLoad);
        return keys.reduce((dl, key) => {
            const value = spreadDailyLoad[key];
            if (dailyLoadKeys.includes(key))
                return Object.assign(Object.assign({}, dl), { [key]: value });
            if (loadInternalExtraInfosKeys.includes(key)) {
                return Object.assign(Object.assign({}, dl), { internal_extra_infos: Object.assign(Object.assign({}, dl === null || dl === void 0 ? void 0 : dl.internal_extra_infos), { [key]: value }) });
            }
            return Object.assign(Object.assign({}, dl), { extra_infos: Object.assign(Object.assign({}, dl === null || dl === void 0 ? void 0 : dl.extra_infos), { [key]: value }) });
        }, {});
    });
}
const ofTooltipMatch = _.sortBy([
    // Default params
    { label: "PDC", field: "pdc" },
    { label: "Statut de l'opération", field: "op_status" },
    { label: "Référence article", field: "ref_article" },
    { label: "Num OF", field: "of_id" },
    //Mostly for Orano
    { label: "Date demande", field: "datelancement", type: "date" },
    { label: "Demandeur", field: "demandeur" },
    { label: "Client", field: "client" },
    { label: "Installation", field: "installation" },
    { label: "Unité productrice", field: "unite_productrice" },
    { label: "Centre de coût/EOTP", field: "centre_de_cout" },
    { label: "Code déchet", field: "code_dechet" },
    { label: "Libellé du déchet", field: "libelle_du_dechet" },
    { label: "Quantité", field: "quantite" },
    { label: "Type de saisie producteur", field: "type_saisie" },
    { label: "Masse U5", field: "masse_u5" },
    {
        label: "Date enlèvement souhaitée",
        field: "date_enlevement_souhaitee",
        type: "date",
    },
    { label: "Localisation", field: "localisation" },
    { label: "Code régime MN", field: "code_regime_mn" },
    { label: "Fiches suiveuses", field: "fiches_suiveuses" },
    { label: "Remarques", field: "remarques" },
    //Mostly for Asquini
    { label: "Phase", field: "phase" },
    { label: "Nature", field: "nature" },
    { label: "CDC", field: "cdc" },
    { label: "Article", field: "article" },
    { label: "Référence", field: "reference" },
    { label: "Libellé", field: "libelle" },
    { label: "Famille", field: "famille" },
    { label: "Ss famille", field: "ss_famille" },
    { label: "TP", field: "tp" },
    { label: "TU", field: "tu" },
    { label: "Lot", field: "lot" },
    { label: "Interne", field: "interne" },
    { label: "TT", field: "tt" },
    { label: "Statut", field: "statut" },
    { label: "Début phase charge", field: "debut_phase_charge", type: "date" },
    { label: "Fin phase", field: "fin_phase", type: "date" },
    { label: "Fin phase charge", field: "fin_phase_charge", type: "date" },
    { label: "Marge", field: "marge" },
    { label: "Quantité bonne", field: "quantite_bonne" },
    { label: "Quantité OF", field: "quantite_of" },
    // {label: "Type", field: "type"},
    { label: "Statut de l'OF", field: "statut_de_lof" },
    //Mostly for Janson
    { label: "GFP", field: "gfp" },
    { label: "N° Commande", field: "n_commande" },
    { label: "Réf. Art.", field: "ref_art" },
    { label: "Des. OF", field: "des_of" },
    { label: "OF Edité", field: "of_edite" },
    { label: "Début OF", field: "debut_of", type: "date" },
    { label: "Etat", field: "etat" },
    { label: "Code Client", field: "code_client" },
    { label: "Année", field: "annee" },
    { label: "Mois", field: "mois" },
    { label: "Semaine", field: "semaine" },
    { label: "Tete", field: "tete" },
    { label: "Jupe", field: "jupe" },
    { label: "ALU", field: "alu" },
    { label: "Désignation opération", field: "designation_operation" },
    { label: "Code commande", field: "code_commande" },
    { label: "Libellé client", field: "libelle_client" },
    { label: "Etat OF", field: "etat_of" },
    { label: "Code société", field: "code_societe" },
    { label: "Type de charge", field: "type_de_charge" },
    { label: "Nombre de pièces", field: "nb_pieces" },
    { label: "Temps de cycle", field: "tps_cycle" },
    { label: "Famille de produit", field: "famille_produit" },
    { label: "Code_famille", field: "code_famille" },
    { label: "Type", field: "type" },
    { label: "Opération", field: "operation" },
    { label: "Description", field: "description" },
    { label: "Type", field: "tps_cycle" },
    { label: "Segmentation", field: "segmentation_group" },
    { label: "Segmentation (précise)", field: "segmentation" },
    //Mostly for Berthelot
    { label: "Date début", field: "date_debut" },
    { label: "Centre de frais", field: "centre_de_frais_poste_de_charge" },
    { label: "Sous-Traitable", field: "sous_traitable" },
    { label: "Rang", field: "rang" },
    { label: "Désignation", field: "designation" },
    { label: "Qté affaire", field: "qte_affaire" },
    { label: "Délai Client", field: "delai_client" },
    { label: "Délai Planning", field: "delai_planning" },
    { label: "Numéro pointage", field: "numero_pointage" },
    { label: "Section", field: "section" },
    { label: "Précédent", field: "precedent" },
    { label: "Suivant", field: "suivant" },
    {
        label: "Ecart Délais Planning-Client",
        field: "ecart_delais_planningclient",
    },
    { label: "Ecart Delai Interne", field: "ecart_delai_interne" },
    { label: "Date Préced.", field: "date_preced" },
    { label: "Ecart", field: "ecart" },
    //Mostly for Lisi
    { label: "Division", field: "division" },
    { label: "Etablissement", field: "etablissement" },
    { label: "Num OF", field: "num_of" },
    { label: "Num OP", field: "num_op" },
    { label: "Statut OP", field: "statut_op" },
    { label: "Code Article", field: "code_article" },
    { label: "Priorité", field: "priorite" },
    { label: "Par Combien", field: "par_combien" },
    { label: "Designation OP", field: "designation_op" },
    { label: "Tps Fabrication", field: "tps_fabrication" },
    { label: "Type Statut", field: "type_statut" },
    { label: "Production Factor Group", field: "production_factor_group" },
    { label: "Type Structure", field: "type_structure" },
    { label: "EH ou KG", field: "eh_ou_kg" },
    { label: "Type OF", field: "type_of" },
    { label: "Poids", field: "poids" },
    { label: "OF ou POF", field: "of_ou_pof" },
    { label: "Num Boucle", field: "num_boucle" },
    { label: "UAP", field: "uap" },
    { label: "Macro-Gamme", field: "macrogamme" },
    //Mostly for Lisi - LIXUS
    { label: "Num OP", field: "num_operation" },
    { label: "Num OF Client", field: "num_of_client" },
    { label: "Référence Article", field: "reference_article" },
    { label: "Nom Opération", field: "nom_operation" },
    { label: "Ligne", field: "ligne" },
    //Mostly for Lisi Médical
    { field: "familles_pdp", label: "Familles PDP" },
    { field: "gap", label: "Gap" },
    { field: "centre_charge", label: "Centre de charge" },
    { field: "machine", label: "Machine" },
    { field: "code_gamme", label: "Code gamme" },
    { field: "description_1", label: "Description 1" },
    { field: "description_2", label: "Description 2" },
    { field: "description_3", label: "Description 3" },
    { field: "cellule_de_fabrication", label: "Cellule de fabrication" },
    { field: "num_operation", label: "Num opération" },
    { field: "temps_reglage", label: "Temps réglage" },
    { field: "temps_dexecution", label: "Temps d'exécution" },
    { field: "temps_machine", label: "Temps machine" },
    { field: "gere_en_temps_machine", label: "Géré en temps machine" },
    { field: "transfert", label: "Transfert" },
    { field: "charge_initiale", label: "Charge initiale" },
    //Mostly for Lisi Médical
    { field: "site_mo", label: "Site MO" },
    { field: "nom_article", label: "Nom article" },
    { field: "nombre_de_pieces", label: "Nombre de pièces" },
    //Mostly for Vaucher
    { field: "work_center", label: "POSTE DE TRAVAIL" },
    { field: "material", label: "ARTICLE" },
    { field: "material_text", label: "DESIGNATION" },
    { field: "order_category", label: "CATEGORIE D'ORDRE" },
    { field: "production_order_number", label: "ORDRE DE FABRICATION" },
    { field: "plannedorder_number", label: "ORDRE PLANIFIE" },
    { field: "operation_number", label: "OPERATION" },
    { field: "operation_status", label: "STATUT D'OPERATION" },
    { field: "components_in_place", label: "AU POSTE" },
    { field: "remaining_setup_capacity", label: "CHARGE REGLAGE RESTANTE" },
    {
        field: "remaining_processing_capacity",
        label: "CHARGE TRAITEMENT THEORIQUE RESTANTE",
    },
    {
        field: "remaining_teardown_capacity",
        label: "CHARGE DEMONTAGE RESTANTE",
    },
    { field: "earliest_start_date", label: "DATE DE DEBUT AU PLUS TOT" },
    { field: "latest", label: "DATE DE DEBUT AU PLUS TARD" },
    //Mostly for Nexteam BU AT
    { field: "id_article", label: "Id Article" },
    { field: "libelle_art", label: "Libelle Art" },
    { field: "ref", label: "REF" },
    { field: "date_debut_of", label: "Date Debut OF" },
    { field: "n_prior_couv", label: "N Prior Couv" },
    { field: "id_ofs", label: "Id Ofs" },
    { field: "qte_lancee", label: "Qte Lancee" },
    { field: "tps_prepa", label: "Tps Prepa" },
    { field: "tps_usi", label: "Tps Usi" },
    { field: "charge_h", label: "Charge (h)" },
    { field: "id_ph", label: "Id PH" },
    { field: "cdc", label: "CDC" },
    { field: "nb_associated_ofs", label: "Nb d'OFs associés" },
    { field: "famille_planif", label: "Famille Planif" },
    { field: "statut_phase", label: "Statut Phase" },
    { field: "type_charge", label: "Type charge" },
    { field: "date_fin", label: "Date fin" },
    { field: "statut_ordre", label: "Statut Ordre" },
    //Mostly for Nexteam BU Services
    { field: "ordre", label: "Ordre" },
    { field: "gestionnaire", label: "Gestionnaire" },
    { field: "gestionnaire_mrp", label: "Gestionnaire MRP" },
    { field: "type_avion", label: "Type Avion" },
    { field: "article_reference", label: "Article - Référence" },
    { field: "article_libelle", label: "Article - Libellé" },
    { field: "clients", label: "Clients" },
    { field: "of_statut_global", label: "OF - Statut Global" },
    //Mostly for Nexteam BU Engine
    { field: "statutop", label: "Statut OP" },
    { field: "statutcde", label: "Statut Commande" },
    { field: "qte_rebutee", label: "Quantité rebutée" },
    { field: "n_of", label: "N° OF" },
    // Nexteam BU Equipments Olemps
    { field: "customer", label: "Client" },
    //Mostly for Lisi Delle
    { field: "atelier", label: "Atelier" },
    { field: "zone_planif", label: "Zone planif" },
    { field: "n_fournisseur", label: "N° Fournisseur" },
    { field: "groupe_techno", label: "Groupe Techno" },
    { field: "abc", label: "ABC" },
    { field: "n_prog", label: "N° Prog" },
    { field: "type_ordre", label: "Type Ordre" },
    { field: "ope", label: "Opé." },
    { field: "tps_restant", label: "Tps restant" },
    { field: "poids_kg", label: "Poids (Kg)" },
    {
        field: "valeur_operation_incl_produits_achetes",
        label: "valeur opération incl. produits achetés",
    },
    { field: "valeur_matiere", label: "Valeur matière" },
    { field: "valeur_composant", label: "Valeur composant" },
    {
        field: "valeur_autres_articles_achetes",
        label: "valeur autres articles achetés",
    },
    { field: "nb_exec_prevu", label: "nb exec prevu" },
    { field: "datelancement_of", label: "Date de début de l'OF" },
    { field: "datesortie_of", label: "Date de fin de l'OF" },
    //Mostly for Lisi VDR
    { field: "ligne_produit", label: "Ligne produit" },
    { field: "_reference", label: "Référence" },
    { field: "matiere", label: "Matière" },
    { field: "diametre", label: "Diamètre" },
    { field: "operation_en_cours", label: "Opération en cours" },
    { field: "date_debut_op_prevue", label: "Date initiale", type: "date" },
    { field: "forme_tete", label: "Forme de tête" },
    { field: "statut_composant", label: "Statut composant" },
    { field: "longueur_sous_tete", label: "Longueur sous tête" },
    { field: "texte_operation", label: "Texte opération" },
    { field: "charge_temps_poids", label: "Charge temps/poids" },
    { field: "diam_depart", label: "Diam. départ" },
    //Mostly for J3L
    { field: "famille_pic", label: "Famille PIC" },
    { field: "site_de_production", label: "Site de production" },
    //Mostly for LISI COI
    { field: "article_type", label: "Article type" },
    { field: "material_availability", label: "Material Availability" },
    //Mostly for LISI BAR
    { field: "ref_compl", label: "Référence" },
    { field: "statut_ope", label: "Statut Opération" },
    { field: "groupe_accord", label: "Groupe Accord" },
    { field: "donneur_ordre", label: "Donneur Ordre" },
    //Mostly for ADDEV, demo
    { field: "ferme_vs_prev", label: "Ferme vs. Prévisionnel" },
    { field: "lien_avec_commande", label: "Lien avec commande" },
    { field: "statut_mp", label: "Statut MP" },
    //Mostly for Tecalemit
    { field: "statut_2", label: "Statut 2" },
    { field: "refaffdes", label: "Programme client" },
    { field: "refcust", label: "Référence client" },
    //Mostly for Horlyne
    { field: "client_nom", label: "Nom Client" },
    //Mostly for Varinor
    { field: "typologie", label: "Typologie" },
    { field: "masse_bd_est", label: "Masse BD Est." },
    { field: "alliages", label: "Alliages" },
    { field: "dimensions", label: "Dimensions" },
    { field: "n_coulee", label: "N° Coulée" },
    { field: "racine_of", label: "Racine OF" },
    { field: "couleur", label: "Couleur" },
    { field: "prio", label: "Prio" },
    //Others
    { field: "is_late", label: "En retard ?" },
    { field: "group_key", label: "Clé de grouping" },
    { field: "order_number", label: "Numéro de commande" },
], "label");
function getOFUnit(of, fallback = DEFAULT_LOAD_UNITS.OF) {
    if (!(of === null || of === void 0 ? void 0 : of.unite_of))
        return fallback;
    return of.unite_of;
}
function getOPUnit(op, sector, fallback = DEFAULT_LOAD_UNITS.OP) {
    if (op === null || op === void 0 ? void 0 : op.unite_op)
        return op.unite_op;
    if (op === null || op === void 0 ? void 0 : op.unite)
        return op.unite;
    if (sector === null || sector === void 0 ? void 0 : sector.unite)
        return sector.unite;
    return fallback;
}
function getOFDisplayedQuantityKeys(of) {
    var _a, _b;
    const { displayed_quantite, quantite_of, operations = [] } = of || {};
    const quantite = (_b = quantite_of !== null && quantite_of !== void 0 ? quantite_of : (_a = _.maxBy(operations, ({ load }) => +(load || 0))) === null || _a === void 0 ? void 0 : _a.load) !== null && _b !== void 0 ? _b : 0;
    const unit = getOFUnit(operations[0]);
    return {
        quantite,
        unit,
        displayed_quantite: displayed_quantite || `${quantite} ${unit}`,
    };
}
/**
 * Compares objects to return a subtract of newObject only containing the keys which value differ from originalObject
 * Warning: this function will return a key that is a function even if equal (should not be met for our usage)
 *
 * @returns an object containing the keys/values pairs from newObject which values are different between newObject and originalObject
 */
function getObjectDiff(newObject, originalObject) {
    function changes(newObject, originalObject) {
        return _.transform(newObject, function (result, value, key) {
            if (!_.isEqual(value, originalObject[key])) {
                result[key] =
                    _.isObject(value) && _.isObject(originalObject[key])
                        ? changes(value, originalObject[key])
                        : value;
            }
        });
    }
    return changes(newObject, originalObject);
}
const roundNumberIfDefined = (num, decimals = 2) => {
    if (num == null)
        return;
    if (num === "")
        return;
    if (isNaN(+num))
        return;
    return _.round(+num, decimals);
};
const errorFormatter = (error) => {
    try {
        const _a = error, { message: error_message, stack: error_stack, name: error_name } = _a, rest = __rest(_a, ["message", "stack", "name"]);
        return Object.assign({ error_message, error_stack, error_name }, rest);
    }
    catch (e) {
        return { error: e };
    }
};
const toFinite = (num) => {
    const n = num !== null && num !== void 0 ? num : 0;
    return isFinite(+n) ? +n : 0;
};
const cleanNullishValuesInObject = (obj) => {
    return _.pickBy(obj, (value) => value !== null && value !== undefined);
};
const validateEmail = (email) => {
    return toSafeString(email).match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
};
function capitalizeFirstLetter(val) {
    return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}
/**
 * Remove reserved regex characters from a string so that they don't
 * affect search results
 */
function escapeRegExp(str) {
    if (!str)
        return "";
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function isUnitKilos(unit) {
    if (!unit)
        return false;
    return ["kg", "kilo"].includes(`${unit}`.toLowerCase().trim());
}
function getConditionalQuantity(originalQuantity, params) {
    const { isKilo = false, kiloQuantity = originalQuantity, shouldCountEntriesForQuantity = false, } = params;
    if (shouldCountEntriesForQuantity)
        return 1;
    if (isKilo && kiloQuantity != null)
        return kiloQuantity;
    return originalQuantity || 0;
}
// TODO: replace usage wherever similar logic is applied
function filterSimulationsOnTeam(simulationsArray, team) {
    if (!(team === null || team === void 0 ? void 0 : team.id))
        return simulationsArray;
    return simulationsArray.filter((simulation) => {
        var _a;
        if (!((_a = simulation.team_ids) === null || _a === void 0 ? void 0 : _a.length))
            return true;
        return simulation.team_ids.includes(team.id);
    });
}
function getOperationsAggregatedQuantities(operations, params) {
    const defaultBatchId = "__no_batch__";
    const { deduplicate_by_sequence } = params || {};
    const mappedOperations = operations.map(({ batch_id, quantite_of, op_duration, quantite_op, quantite, quantite_op_2, load, op_sequence, of_id, }) => ({
        batch_id,
        quantite_of: (quantite_of && +quantite_of) || 0,
        quantite_op: (quantite_op && +quantite_op) ||
            (quantite && +quantite) ||
            (load && +load) ||
            0,
        op_duration: (op_duration && +op_duration) || 0,
        quantite_op_2: (quantite_op_2 && +quantite_op_2) || 0,
        op_sequence,
        of_id,
    }));
    const operationsByBatchId = _.groupBy(mappedOperations, ({ batch_id }) => batch_id || defaultBatchId);
    const { quantiteOf, quantiteOp, opDuration, quantiteOp2 } = Object.entries(operationsByBatchId).reduce((acc, [batch_id, ops]) => {
        let maxQuantiteOp = 0, maxOpDuration = 0;
        const isBatch = batch_id !== defaultBatchId;
        // deduplicated ops by op_sequence
        // (je passe par un feature flag pour le moment mais logiquement ça devrait toujours être appliqué)
        const deduplicatedOps = deduplicate_by_sequence
            ? _.uniqBy(ops, ({ of_id, op_sequence }) => `${of_id} - ${op_sequence}`)
            : ops;
        for (const op of deduplicatedOps) {
            acc.quantiteOp2 += op.quantite_op_2;
            acc.quantiteOf += op.quantite_of;
        }
        // for quantite_op, we want to count them on all ops
        for (const op of ops) {
            if (isBatch) {
                maxQuantiteOp = Math.max(maxQuantiteOp, op.quantite_op);
                maxOpDuration = Math.max(maxOpDuration, op.op_duration);
            }
            else {
                acc.quantiteOp += op.quantite_op;
                acc.opDuration += op.op_duration;
            }
        }
        if (isBatch) {
            acc.quantiteOp += maxQuantiteOp;
            acc.opDuration += maxOpDuration;
        }
        return acc;
    }, { quantiteOf: 0, quantiteOp: 0, opDuration: 0, quantiteOp2: 0 });
    return {
        quantite_of: _.round(quantiteOf, 2),
        quantite_op: _.round(quantiteOp, 2),
        op_duration: _.round(opDuration, 2),
        quantite_op_2: _.round(quantiteOp2, 2),
    };
}
function getStoredIdentifierFromConcatenatedValues(...values) {
    return values.join(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION);
}
function getDisplayedIdentifierFromConcatenatedValues(identifier, params) {
    const { omittedIndexes = [], separator = "" } = params || {};
    const splitIdentifier = identifier.split(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION);
    if (!omittedIndexes.length)
        return splitIdentifier.join(separator);
    return splitIdentifier
        .filter((_, index) => !omittedIndexes.includes(index))
        .join(separator);
}
function hasSomeLoadImportIdObj(importIds) {
    return importIds.some(({ collection, id }) => [IMPORT_TYPES.LOAD, IMPORT_TYPES.CUSTOMERS_ORDERS].includes(collection) && id);
}
function sleep(time) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve) => setTimeout(resolve, time));
    });
}

// eslint-disable-next-line no-redeclare
function parseDate(date = undefined, convertToDate = false, keepLocalTime = false, dateFormat = "YYYY-MM-DD HH:mm:ss.SSS") {
    let datestr;
    if (date === null || date === void 0 ? void 0 : date.toDate)
        datestr = date.toDate();
    else if ((date === null || date === void 0 ? void 0 : date.seconds) !== undefined)
        datestr = date.seconds * 1000;
    else if ((date === null || date === void 0 ? void 0 : date._seconds) !== undefined)
        datestr = date._seconds * 1000;
    else if (moment(date || undefined).isValid())
        datestr = date || undefined;
    else {
        console.error("an invalid date was provided to parseDate, falling back to today", date, typeof date);
        datestr = moment().format("YYYY-MM-DD HH:mm:ss.SSS");
    }
    let dateMoment = moment.utc(datestr);
    if (keepLocalTime)
        dateMoment = moment(datestr);
    const parsedDate = dateMoment.format(dateFormat);
    if (convertToDate)
        return new Date(parsedDate);
    return parsedDate;
}
/**
 * Calculates the number of workdays between two dates, including the starting and ending dates.
 * @param start_date - The start date in the format YYYY-MM-DD.
 * @param end_date - The end date in the format YYYY-MM-DD.
 * @param calendar - An optional array of Calendar objects representing changes to the work schedule on a given schedule.
 * @param maille - An optional moment unit of time value representing the granularity of the calculation (default is "month").
 * @param todayDate - An optional date string in the format YYYY-MM-DD representing the current date (default is "").
 * @param rawTotal - An optional boolean value indicating whether to return the raw total days or the calculated workdays (default is false).
 * @returns An object containing the number of workdays, saturdays, sundays, holiday days, total days, and an array of weekdays.
 */
const nbWorkDays = function (start_date, //format YYYY-MM-DD
end_date, //format YYYY-MM-DD
calendar = [], maille = "month", todayDate = "", //format YYYY-MM-DD
rawTotal = false) {
    const defaultResult = {
        workdays: 0,
        saturdays: 0,
        sundays: 0,
        holiday_days: 0,
        total_days: 0,
        weekdays: new Array(7).fill(0),
    };
    try {
        if (!start_date || !end_date)
            return defaultResult;
        if (!moment(start_date, "YYYY-MM-DD").isValid() ||
            !moment(end_date, "YYYY-MM-DD").isValid()) {
            console.error("nbWorkdays date format is not valid", {
                start_date,
                end_date,
                maille,
                todayDate,
                rawTotal,
            });
            return defaultResult;
        }
        if (end_date < start_date)
            return defaultResult;
        let workdays = 0, saturdays = 0, sundays = 0, holiday_days = 0;
        //We fetch all holidays
        const [start_year] = (start_date || "").split("-");
        const [end_year] = (end_date || "").split("-");
        let holidays = [];
        for (let an = parseInt(start_year); an <= parseInt(end_year); an++)
            holidays = [...holidays, ...JoursFeries(an)];
        if (todayDate &&
            moment(end_date || undefined)
                .endOf(maille)
                .format("YYYY-MM-DD") < todayDate) {
            return {
                workdays,
                saturdays,
                sundays,
                holiday_days,
                total_days: 0,
                weekdays: new Array(7).fill(0),
            };
        }
        let currentDate = ___default.max([start_date, todayDate || "1970-01-01"]);
        const changes = mapCalendarToWorkdays((calendar || []).filter((change) => !change.is_child_cal &&
            change.date >= currentDate &&
            change.date <= end_date));
        //group the changes by day
        const changesByDay = ___default.groupBy(___default.orderBy(changes, "updated_at", "desc"), "date");
        const weekdays = new Array(7).fill(0);
        while (currentDate <= end_date) {
            // We want it from 0 to 6 without influence from locale start of week
            const weekDay = moment(currentDate).isoWeekday() - 1;
            const changeMatches = changesByDay[currentDate];
            const changeMatch = ___default.get(changeMatches, 0);
            const isHolidayDay = holidays.some((x) => x === currentDate);
            if (changeMatch) {
                if (changeMatch.isWorkday) {
                    weekdays[weekDay]++;
                    if (isHolidayDay)
                        holiday_days++;
                    else if (weekDay === 6)
                        sundays++;
                    else if (weekDay === 5)
                        saturdays++;
                    workdays++;
                }
            }
            else if (weekDay !== 6 && weekDay !== 5 && !isHolidayDay) {
                weekdays[weekDay]++;
                workdays++;
            }
            else if (isHolidayDay)
                holiday_days++;
            else if (weekDay === 5)
                saturdays++;
            else if (weekDay === 6)
                sundays++;
            currentDate = moment(currentDate).add(1, "days").format("YYYY-MM-DD");
        }
        const total_days = ___default.sum(weekdays);
        /**
         * FIXME: total_days does NOT represent the total amount of days between the two dates
         * its value currently has to be retrieved by summing workdays + saturdays + sundays + holiday_days
         */
        if (!rawTotal) {
            return {
                workdays,
                saturdays,
                sundays,
                holiday_days,
                total_days: total_days || 1,
                weekdays,
            };
        }
        else
            return {
                workdays: undefined,
                saturdays: undefined,
                sundays: undefined,
                holiday_days: undefined,
                total_days,
                weekdays,
            };
    }
    catch (e) {
        console.error("nbWorkdays error", e, {
            start_date,
            end_date,
            calendar: calendar === null || calendar === void 0 ? void 0 : calendar.length,
            maille,
            todayDate,
            rawTotal,
        });
        return defaultResult;
    }
};
const workDaysSum = function (startDate, endDate, orderedCalendars = []) {
    var _a, _b;
    return (((_b = (_a = nbWorkDays(startDate, endDate, orderedCalendars)) === null || _a === void 0 ? void 0 : _a.weekdays) === null || _b === void 0 ? void 0 : _b.reduce((previousValue, currentValue) => previousValue + currentValue, 0)) || 0);
};
const getWorkDays = function (date, //format YYYY-MM-DD
calendar = [], maille = "month", dateMin = "", //format YYYY-MM-DD
todayDate = "") {
    const [year, month, day] = (date || "").split("-");
    let workdays = 0, saturdays = 0, sundays = 0, holiday_days = 0;
    const holidays = JoursFeries(+year) || [];
    if (todayDate &&
        moment(date || undefined)
            .endOf(maille)
            .format("YYYY-MM-DD") < todayDate)
        return { workdays, saturdays, sundays, holiday_days, total_days: 0 };
    let currentDate = ___default.max([date, dateMin || "1970-01-01"]);
    const week = moment(currentDate).isoWeek();
    const quarter = moment(currentDate).quarter();
    const changes = mapCalendarToWorkdays((calendar || []).filter((change) => !change.is_child_cal &&
        change.date >= currentDate &&
        change.date <= moment(currentDate).endOf(maille).format("YYYY-MM-DD")));
    const weekdays = new Array(7).fill(0);
    while (maille === "day"
        ? currentDate.split("-")[2] === day
        : maille === "week"
            ? moment(currentDate).isoWeek() === week
            : maille === "year"
                ? currentDate.split("-")[0] === year
                : maille === "quarter"
                    ? moment(currentDate).quarter() === quarter
                    : currentDate.split("-")[1] === month) {
        const weekDay = moment(currentDate).weekday();
        const changeMatches = changes.filter((change) => (change === null || change === void 0 ? void 0 : change.date) === currentDate);
        const changeMatch = ___default.get(___default.orderBy(changeMatches, "updated_at", "desc"), 0);
        const isHolidayDay = holidays.some((x) => x === currentDate);
        if (changeMatch) {
            if (changeMatch.isWorkday) {
                weekdays[weekDay]++;
                if (isHolidayDay)
                    holiday_days++;
                else if (weekDay === 6)
                    sundays++;
                else if (weekDay === 5)
                    saturdays++;
                else
                    workdays++;
            }
        }
        else if (weekDay !== 6 && weekDay !== 5 && !isHolidayDay) {
            weekdays[weekDay]++;
            workdays++;
        }
        currentDate = moment(currentDate).add(1, "days").format("YYYY-MM-DD");
    }
    const total_days = ___default.sum([workdays, saturdays, sundays, holiday_days]) || 0;
    return { workdays, saturdays, sundays, holiday_days, total_days, weekdays };
};
/**
 * Checks if a given date is a workday.
 * @param date - The date to check in the format YYYY-MM-DD.
 * @param calendar - An optional array of calendar entries representing working and non-working days.
 * @param excludeHolidays : a boolean representing the exclusion of holidays from the workdays logic
 * @returns A boolean indicating whether the given date is a workday or not.
 */
const isWorkDay = function (date, //format YYYY-MM-DD
calendar = [], excludeHolidays = false) {
    if (!date || !moment(date, "YYYY-MM-DD").isValid())
        return false;
    const strDate = moment(date).format("YYYY-MM-DD");
    const [year] = (strDate || "").split("-");
    if (year === "1970")
        return true;
    const changes = mapCalendarToWorkdays((calendar || []).filter((change) => !change.is_child_cal && change.date === strDate));
    const changeMatch = ___default.get(___default.orderBy(changes, "updated_at", "desc"), 0);
    if (changeMatch)
        return !!changeMatch.isWorkday;
    return !isWeekend(date) && (excludeHolidays ? true : !isHoliday(date));
};
/**
 * Calculates the number of days required to complete a certain number of workdays starting from a given date.
 * @param date - The starting date in the format YYYY-MM-DD.
 * @param workdayCount - The number of workdays to complete.
 * @param calendarObject - An optional object of arrays of calendar entries representing working and non-working days.
 * @param shouldExcludeFirstDate - An optional boolean value indicating whether to exclude the starting date from the calculation (default is false).
 * @returns The number of days (non-workdays included) required to complete the workdays.
 */
const workdayCountToDayCount = function (date, //format YYYY-MM-DD
workdayCount, calendarObject, shouldExcludeFirstDate = false) {
    if (!date || !moment(date, "YYYY-MM-DD").isValid())
        return 0;
    const workdayCountSign = Math.sign(workdayCount);
    let currentDate = moment(date);
    if (shouldExcludeFirstDate)
        currentDate = currentDate.add(workdayCountSign, "days");
    let remainingWorkdays = Math.abs(workdayCount);
    let dayCount = 0;
    while (remainingWorkdays > 0) {
        const formattedDate = currentDate.format("YYYY-MM-DD");
        if (isWorkDay(formattedDate, calendarObject[formattedDate]))
            remainingWorkdays--;
        dayCount++;
        currentDate = currentDate.add(workdayCountSign, "days");
    }
    return workdayCountSign * dayCount;
};
/**
 * applies the same logic as `workdayCountToDayCount` but returns the status of `isWorkDay` for each browsed date\
 * this is meant to enhance execution time by storing workdays state in an object in the caller of this function\
 * TODO: ideally use function overloads on `workdayCountToDayCount`
 */
function workdayCountAndWorkdaysToDayCount(date, //format YYYY-MM-DD
workdayCount, calendarObject, shouldExcludeFirstDate = false, workdays = {}) {
    var _a;
    const isWorkdayByDate = {};
    if (!date || !moment(date, "YYYY-MM-DD").isValid())
        return { count: 0, isWorkdayByDate };
    const workdayCountSign = Math.sign(workdayCount);
    let currentDate = moment(date);
    if (shouldExcludeFirstDate)
        currentDate = currentDate.add(workdayCountSign, "days");
    let remainingWorkdays = Math.abs(workdayCount);
    let dayCount = 0;
    while (remainingWorkdays > 0) {
        const formattedDate = currentDate.format("YYYY-MM-DD");
        const isWorkday = (_a = workdays[formattedDate]) !== null && _a !== void 0 ? _a : isWorkDay(formattedDate, calendarObject[formattedDate]);
        if (isWorkday)
            remainingWorkdays--;
        isWorkdayByDate[formattedDate] = isWorkday;
        dayCount++;
        currentDate = currentDate.add(workdayCountSign, "days");
    }
    return { count: workdayCountSign * dayCount, isWorkdayByDate };
}
const parsePeriode = function (periode, calendar = [], monthBorder = "", //format("YYYY-MM-DD")
monthBorderEnd = "", //format("YYYY-MM-DD")
computeWorkdays = true, maille = "month", todayDate = "", //format("YYYY-MM-DD")
rawTotal = false) {
    if (!(periode === null || periode === void 0 ? void 0 : periode.length) || !Array.isArray(periode))
        return { nbJoursOuvres: 1, startDate: null, endDate: null };
    //Works for weeks as well
    const datePeriode = periode.map((dte) => moment(dte || undefined).format("YYYY-MM-DD")); //moment is to handle the case where the date is only "YYYY-MM"
    const dateMin = ___default.min(datePeriode);
    const startDate = ___default.max([dateMin, monthBorder]);
    const isMonth = (periode[0].match(/-/g) || []).length === 1;
    let dateMax = ___default.max(datePeriode);
    dateMax = isMonth
        ? moment(dateMax).endOf("month").format("YYYY-MM-DD")
        : dateMax;
    let endDate = dateMax;
    if (monthBorderEnd)
        endDate = ___default.min([endDate, monthBorderEnd]);
    const { workdays: nbJoursOuvres, saturdays, sundays, holiday_days, total_days, } = computeWorkdays
        ? nbWorkDays(startDate, endDate, calendar, maille, todayDate, rawTotal)
        : {
            workdays: 0,
            saturdays: 0,
            sundays: 0,
            holiday_days: 0,
            total_days: 0,
        };
    return {
        nbJoursOuvres,
        saturdays,
        sundays,
        holiday_days,
        total_days,
        startDate,
        endDate,
    };
};
/**
 * Calculate holidays in a year
 * @param an the year
 * @returns the list of holidays in format YYYY-MM-DD
 */
const JoursFeries = function (an) {
    const JourAn = new Date(an, 0, 1);
    const FeteTravail = new Date(an, 4, 1);
    const Victoire1945 = new Date(an, 4, 8);
    const FeteNationale = new Date(an, 6, 14);
    const Assomption = new Date(an, 7, 15);
    const Toussaint = new Date(an, 10, 1);
    const Armistice = new Date(an, 10, 11);
    const Noel = new Date(an, 11, 25);
    const G = an % 19;
    const C = Math.floor(an / 100);
    const H = (C - Math.floor(C / 4) - Math.floor((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H -
        Math.floor(H / 28) *
            (1 -
                Math.floor(H / 28) *
                    Math.floor(29 / (H + 1)) *
                    Math.floor((21 - G) / 11));
    const J = (an * 1 + Math.floor(an / 4) + I + 2 - C + Math.floor(C / 4)) % 7;
    const L = I - J;
    const MoisPaques = 3 + Math.floor((L + 40) / 44);
    const JourPaques = L + 28 - 31 * Math.floor(MoisPaques / 4);
    const Paques = new Date(an, MoisPaques - 1, JourPaques);
    const LundiPaques = new Date(an, MoisPaques - 1, JourPaques + 1);
    const Ascension = new Date(an, MoisPaques - 1, JourPaques + 39);
    const Pentecote = new Date(an, MoisPaques - 1, JourPaques + 49);
    const LundiPentecote = new Date(an, MoisPaques - 1, JourPaques + 50);
    return [
        JourAn,
        Paques,
        LundiPaques,
        FeteTravail,
        Victoire1945,
        Ascension,
        Pentecote,
        LundiPentecote,
        FeteNationale,
        Assomption,
        Toussaint,
        Armistice,
        Noel,
    ].map((date) => moment(date).format("YYYY-MM-DD"));
};
//Periods functions
const divideAndConquer = function (filteredMacroEvents, start_date, //format YYYY-MM-DD
end_date, //format YYYY-MM-DD
sharedParams = {}) {
    const { maille = "month", calendar = [], simulation = {}, rawTotal = false, } = sharedParams;
    let datesArr = [
        ...filteredMacroEvents.map((x) => x.start_date),
        ...filteredMacroEvents.map((x) => moment(x.end_date).add(1, "days").format("YYYY-MM-DD")),
    ];
    if (start_date && moment(start_date).isValid())
        datesArr.push(start_date);
    if (end_date && moment(end_date).isValid())
        datesArr.push(moment(end_date).add(1, "days").format("YYYY-MM-DD"));
    datesArr = datesArr.sort();
    datesArr = ___default.uniq(datesArr); //remove duplicates
    if (!datesArr.length)
        return [];
    //presort macro events
    const sortedMacroEvents = ___default.orderBy(filteredMacroEvents, (o) => parseDate(o.updated_at), ["desc"]);
    const result = [];
    for (let i = 1; i < datesArr.length; i++) {
        const startDate = datesArr[i - 1];
        const endDate = moment(datesArr[i])
            .subtract(1, "days")
            .format("YYYY-MM-DD");
        const includesNew = startDate >= start_date && endDate <= end_date;
        const oldEvents = sortedMacroEvents.filter((x) => {
            return startDate >= x.start_date && endDate <= x.end_date;
        });
        const workdays = nbWorkDays(startDate, endDate, calendar, maille, (simulation === null || simulation === void 0 ? void 0 : simulation.forceToday) || "1970-01-01", rawTotal);
        result.push({ startDate, endDate, includesNew, oldEvents, workdays });
    }
    return result;
};
/**
 * returns an array of numerical week and year numbers within the range
 * of start & end dates of @period
 * handles the new year reset of week number
 * @param period : an array of strings formatted as recognizable dates by moment
 * @returns an array of {week: number, year: number} objects
 */
const yearAndWeekArrayFromPeriod = (period, extraWeek = true) => {
    const [startDate, tempEndDate] = period || [];
    if (!startDate || !tempEndDate)
        return [];
    let endDate = tempEndDate;
    if (extraWeek) {
        endDate = moment(endDate)
            .endOf("week")
            .add(1, "weeks")
            .format(DATE_DEFAULT_FORMAT);
    }
    const meshes = "weeks";
    const length = Math.ceil(moment(endDate).diff(startDate, meshes, true));
    const defined_periods = [...new Array(length + 1)].reduce((acc, _x, i) => {
        const start_date = moment(startDate)
            .add(i, meshes)
            .startOf("week")
            .format(DATE_DEFAULT_FORMAT);
        if (start_date > endDate)
            return acc;
        const currWeek = moment(start_date).isoWeek();
        const currYear = moment(start_date).isoWeekYear();
        acc.push({ week: currWeek, year: currYear });
        return acc;
    }, []);
    return defined_periods;
};
const momentIsHoliday = (date) => {
    const holidays = JoursFeries(date.year());
    const formattedDate = date.format("YYYY-MM-DD");
    return holidays.includes(formattedDate);
};
const momentIsWeekend = (date) => [6, 7].includes(date.isoWeekday());
/**
 * handler with common logic to be passed for date-related functions
 * this is intended to work for function which first argument is a date passed as a string or a moment object
 * this handler will invoke the original function with the argument as a moment object to simplify further logic
 */
const dateHandler = {
    apply: (target, thisArg, argumentsList) => {
        const [date] = argumentsList;
        const momentDate = moment(date);
        if (!moment(momentDate).isValid())
            return false;
        // return the original invoke with
        return target.apply(thisArg, [momentDate, ...argumentsList.slice(1)]);
    },
};
const isHoliday = new Proxy(momentIsHoliday, dateHandler);
const isWeekend = new Proxy(momentIsWeekend, dateHandler);
/**
 * returns the number of days "missing" between an array of dates
 * e.g.
 * ["2023-02-02", "2023-02-03", "2023-02-06", "2023-02-07", "2023-02-09"] => returns 3 (4, 5 and 8 missing)
 * @param dates : an array of moment date objects
 * @returns : a number representing the number of days missing
 */
const getMissingDays = (dates) => {
    const momDates = Array.from(dates.sort(), (date) => moment(date));
    let missingDays = 0;
    for (let i = 1; i < momDates.length; i++)
        missingDays += momDates[i].diff(momDates[i - 1], "days") - 1;
    return missingDays;
};
/**
 * get an array of days off (= where no work is done) between two dates
 * @param period : an array of strings formatted as recognizable dates by moment
 * @param excludeHolidays : a boolean representing the exclusion of holidays from the workdays logic
 * @returns an array of string formatted with the app default format ("YYYY-MM-DD") representing the off days between two dates
 */
const getOffDays = (period, excludeHolidays = true) => {
    const [startDate, endDate] = Array.from(period, (date) => moment(date));
    if (startDate.isAfter(endDate))
        return [];
    const offDays = [];
    while (startDate <= endDate) {
        if (!isWorkDay(startDate, [], excludeHolidays))
            offDays.push(startDate.format("YYYY-MM-DD"));
        startDate.add(1, "day");
    }
    return offDays;
};
const hasWorkdaysPeriod = (period) => {
    if (!(period === null || period === void 0 ? void 0 : period.workdays))
        return false;
    return period.workdays.total_days > 0;
};
const getDatesFromPeriod = ({ temporal_scale, period, }) => {
    //type SimulationPeriod
    const nbPeriodToSubstract = period === "current" ? 0 : period === "last" ? 1 : 0;
    const day = moment().day();
    const diff = day === 1 || day === 0 ? day + 2 : 1;
    const previousBusinessDay = moment()
        .subtract(diff, "days")
        .format("YYYY-MM-DD");
    const startDate = temporal_scale === "day" && period === "last"
        ? previousBusinessDay
        : moment()
            .startOf(temporal_scale)
            .subtract(nbPeriodToSubstract, temporal_scale)
            .format("YYYY-MM-DD");
    const endDate = moment(startDate)
        .add(1, temporal_scale)
        .subtract(1, "days")
        .format("YYYY-MM-DD");
    return { startDate, endDate };
};
/**
 * function used to display the date at specific places within the app\
 * this is only used by the front but has been added in the shared-module
 * because of the lack of a date utils file & it being pure
 */
const getDisplayedPeriod = (date, isMonthView, locale = "fr", fullText = false) => {
    const weekPrefix = locale === "fr"
        ? fullText
            ? "Semaine "
            : "S."
        : fullText
            ? "Week "
            : "W.";
    return capitalizeFirstLetter(moment(date).format(isMonthView ? "MMMM" : `[${weekPrefix}]ww`));
};
function getSectorOrderedCalendars(sector, calendars) {
    if (!(calendars === null || calendars === void 0 ? void 0 : calendars.length))
        return [];
    let calendarsOfInterest = [];
    if (sector) {
        const { id, secteur_id } = sector;
        const sectorID = secteur_id !== null && secteur_id !== void 0 ? secteur_id : id;
        calendarsOfInterest = calendars.filter((calendar) => {
            var _a;
            const calendarType = ((_a = levelCorresp.find(({ collection, type }) => collection === calendar.collection || type === calendar.type)) === null || _a === void 0 ? void 0 : _a.type) || calendar.type;
            if (!calendarType)
                return false;
            // sector calendars
            if (!calendar.is_global && calendar.secteur_id === sectorID)
                return true;
            // parents calendars
            if (calendar.secteur_id === sector[`${calendarType}_id`])
                return true;
            return false;
        });
    }
    else {
        // site-level calendars
        calendarsOfInterest = calendars.filter((calendar) => calendar.type === "site");
    }
    return getSortedUniqCalendarsItems(calendarsOfInterest);
}
function mapCalendarToWorkdays(calendar) {
    if (!(calendar === null || calendar === void 0 ? void 0 : calendar.length))
        return [];
    return calendar.map((change) => {
        var _a;
        return ({
            isWorkday: (_a = change.isWorkday) !== null && _a !== void 0 ? _a : !!change.is_workday,
            date: change.date,
            updated_at: change.updated_at,
        });
    });
}
function getSortedUniqCalendarsItems(calendars) {
    const sortedCalendarsItems = ___default.orderBy(calendars, (calendar) => parseDate(calendar.updated_at), "desc");
    return ___default.uniqBy(sortedCalendarsItems, "date");
}
function getNextAvailableDate(date, calendars, params) {
    const { isMovingToPast, isSorted } = params || {};
    let sortedUniqCalendarsItems;
    if (isSorted) {
        sortedUniqCalendarsItems = calendars;
    }
    else {
        sortedUniqCalendarsItems = getSortedUniqCalendarsItems(calendars);
    }
    const momentDate = moment(date);
    while (!isWorkDay(momentDate, sortedUniqCalendarsItems))
        momentDate.add(isMovingToPast ? -1 : 1, "day");
    return momentDate.format(DATE_DEFAULT_FORMAT);
}
/**
 * This function is used to determine the next available date based on a given number of workdays to visit and a list of calendars
 * @param date The original date to start from
 * @param workdayCount The number of workdays that should be visited (usually representing a leadtime)
 * @param calendars An array of calendars that represent closed/opened days for a given sector
 * @param params An object containing optional parameters
 */
function getNextAvailableDateWithWorkdayCount(date, workdayCount, calendars, params) {
    const { isMovingToPast, isSorted } = params || {};
    let sortedUniqCalendarsItems;
    if (isSorted) {
        sortedUniqCalendarsItems = calendars;
    }
    else {
        sortedUniqCalendarsItems = getSortedUniqCalendarsItems(calendars);
    }
    const momentDate = moment(date);
    if (workdayCount === 0) {
        // checking if the current date is a workday
        if (isWorkDay(momentDate, sortedUniqCalendarsItems))
            return momentDate.format(DATE_DEFAULT_FORMAT);
        else {
            // moving to the closest workday
            momentDate.add(isMovingToPast ? -1 : 1, "day");
            return getNextAvailableDateWithWorkdayCount(momentDate, 0, sortedUniqCalendarsItems, { isMovingToPast, isSorted: true });
        }
    }
    let remainingWorkdayCount = workdayCount;
    while (remainingWorkdayCount > 0) {
        momentDate.add(isMovingToPast ? -1 : 1, "day");
        if (isWorkDay(momentDate, sortedUniqCalendarsItems))
            remainingWorkdayCount--;
    }
    return momentDate.format(DATE_DEFAULT_FORMAT);
}
const getNewDateFromShift = (date, shift, params) => {
    var _a;
    if (!shift)
        return date;
    const newDate = moment(date, DATE_DEFAULT_FORMAT);
    const [hours, minutes] = shift.start_time.split(":");
    newDate.add(+hours, "hours").add(+minutes, "minutes");
    if (params === null || params === void 0 ? void 0 : params.isEndDate)
        newDate.subtract(1, "milliseconds");
    return newDate.format((_a = params === null || params === void 0 ? void 0 : params.dateFormatToReturn) !== null && _a !== void 0 ? _a : DATE_WITH_TIME_FORMAT);
};
function getCorrectFormattedDate({ date, clientFirstShift, dateFormat = DATE_EXPORT_TO_ERP_FORMAT, dateWithTimeFormat = DATE_WITH_TIME_EXPORT_TO_ERP_FORMAT, }) {
    const hasTime = date.length > DATE_DEFAULT_FORMAT.length;
    if (hasTime)
        return moment(date).format(dateWithTimeFormat);
    return clientFirstShift
        ? getNewDateFromShift(date, clientFirstShift, {
            dateFormatToReturn: dateWithTimeFormat,
        })
        : moment(date).format(dateFormat);
}
/**
 * Updates the exported date format in the given data object.
 * If a clientFirstShift object is provided, it uses the start_time property to format the date.
 *
 * @param data - The data object to update.
 * @param clientFirstShift - Optional. An object containing the start_time property used for formatting the date.
 * @returns The updated data object.
 */
const updateExportedDateFormatInPlace = ({ data, clientFirstShift, dateFormat, dateWithTimeFormat, }) => {
    const isDateField = /date|_at/i;
    Object.keys(data).forEach((field) => {
        const isDate = isDateField.test(field);
        if (isDate && data[field])
            data[field] = getCorrectFormattedDate({
                date: data[field],
                clientFirstShift,
                dateFormat,
                dateWithTimeFormat,
            });
    });
    return data;
};
function getDaysArrayBetweenStartEnd(period) {
    const { start_date, end_date } = period;
    if (end_date < start_date)
        return [];
    if (start_date === end_date)
        return [start_date];
    const originalDate = moment(start_date);
    const daysArray = [];
    while (originalDate.isSameOrBefore(moment(end_date), "days")) {
        daysArray.push(originalDate.format(DATE_DEFAULT_FORMAT));
        originalDate.add(1, "days");
    }
    return daysArray;
}
function getWorkdaysArrayBetweenStartEnd(params) {
    const { start_date, end_date, calendarsByDate } = params;
    if (end_date < start_date)
        return [];
    const originalDate = moment(start_date);
    const workdaysArray = [];
    while (originalDate.isSameOrBefore(moment(end_date), "days")) {
        const formattedDate = originalDate.format(DATE_DEFAULT_FORMAT);
        if (isWorkDay(originalDate, calendarsByDate[formattedDate]))
            workdaysArray.push(formattedDate);
        originalDate.add(1, "days");
    }
    return workdaysArray;
}
// TODO: should replace analogous logic within the app with this function
function isPastDate(date) {
    if (!date)
        return false;
    return date < moment().format(DATE_DEFAULT_FORMAT);
}
const datesFromPeriod = function (period) {
    if (typeof period === "string") {
        return {
            startDate: moment(period || undefined).format("YYYY-MM-DD"),
            endDate: moment(period || undefined).format("YYYY-MM-DD"),
        };
    }
    if (!period.length) {
        return {
            startDate: moment().format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
        };
    }
    const startDate = moment(___default.min(period)).format("YYYY-MM-DD");
    const isMonth = (period[0].match(/-/g) || []).length === 1;
    const dateMax = ___default.max(period);
    const endDate = isMonth
        ? moment(dateMax).endOf("month").format("YYYY-MM-DD")
        : moment(dateMax).endOf("day").format("YYYY-MM-DD");
    return { startDate, endDate };
};
function formatDate(date, momentFormat = DATE_DEFAULT_FORMAT) {
    return moment(date).format(momentFormat);
}
function getTimestamp() {
    return moment.utc().format(DATE_WITH_TIME_FORMAT);
}
function getStartOfDateWithTime(date) {
    return moment(date).startOf("day").format(DATE_WITH_DEFAULT_TIME_FORMAT);
}
function getEndOfDateWithTime(date) {
    return moment(date).endOf("day").format(DATE_WITH_DEFAULT_TIME_FORMAT);
}

const schedulingEventFields = [
    {
        original_name: "day_date",
        new_name: "new_date",
        update_name: "date",
        in_extra_infos: false,
    },
    {
        original_name: "fault",
        new_name: "new_fault",
        update_name: "fault",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "tags",
        new_name: "tags",
        update_name: "tags",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "op_tags",
        new_name: "op_tags",
        update_name: "op_tags",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "op_status",
        new_name: "new_status",
        update_name: "status",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "secteur_id",
        new_name: "new_secteur_id",
        update_name: "secteur",
        in_extra_infos: false,
    },
    {
        original_name: "op_order",
        new_name: "new_order",
        update_name: "order",
        in_extra_infos: false,
    },
    {
        original_name: "quantite",
        new_name: "quantite",
        update_name: "quantite",
        in_extra_infos: false,
        pg_name: "load", //special case here since it was renamed
    },
    {
        original_name: "fast_track",
        new_name: "fast_track",
        update_name: "fast_track",
        in_extra_infos: false,
    },
    {
        original_name: "op_fast_track",
        new_name: "op_fast_track",
        update_name: "op_fast_track",
        in_extra_infos: false,
    },
    {
        original_name: "quantity_produced",
        new_name: "quantity_produced",
        update_name: "quantity_produced",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "real_date",
        new_name: "real_date",
        update_name: "real_date",
        in_extra_infos: true,
        is_absolute: true,
    },
    {
        original_name: "real_date_delta",
        new_name: "real_date_delta",
        update_name: "real_date_delta",
        in_extra_infos: true,
        is_absolute: true,
    },
    {
        original_name: "status_done_at",
        new_name: "status_done_at",
        update_name: "status_done_at",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "status_available_at",
        new_name: "status_available_at",
        update_name: "status_available_at",
        in_extra_infos: false,
        is_absolute: true,
    },
    {
        original_name: "status_ongoing_at",
        new_name: "status_ongoing_at",
        update_name: "status_ongoing_at",
        in_extra_infos: false,
        is_absolute: true,
    },
];
/**
 * Calculate the estimated end date of an OF or OP, given its current date
 * and the OF delta.
 *
 * If `return_delta` is true, the function returns the estimated delta in days
 * between the current date and the estimated end date.
 *
 * If `all_ops_done` is true, the function will not correct the estimated end date
 * with the first active OP planned date.
 *
 * If `first_active_op_theoric_date` is given, the function will use it to calculate
 * the estimated end date. If `is_day_date_modified` is true, the function will use
 * the first active OP planned date instead of the theoric date.
 *
 * If `is_day_date_modified` is true and `first_active_op_planned_date` is given,
 * the function will use the planned date to correct the estimated end date.
 *
 * @param current_date - the current date of the OF
 * @param of_delta - the delta in days between the first OP's ERP date and its done date/the current date.
 * @param params.initial_date - the ERP date of the current OP
 * @param params.planned_date - the planned date of the current OP
 * @param params.return_delta - whether to return the estimated delta in days
 * @param params.all_ops_done - whether all operations are done
 * @param params.first_active_op_theoric_date - the ERP date of the first active OP
 * @param params.first_active_op_planned_date - the planned date of the first active OP
 * @param params.is_day_date_modified - whether the day date is modified
 * @returns the estimated end date of the OF, or the estimated delta in days
 */
const getEstimatedEndDate = (current_date, of_delta, params) => {
    const { initial_date, planned_date, return_delta = false, all_ops_done = false, first_active_op_theoric_date, first_active_op_planned_date, is_day_date_modified = false, has_of_started = false, } = params || {};
    // We handle the cases where the OF or the current OP is completely done
    const isCurrentOPDone = initial_date &&
        first_active_op_theoric_date &&
        moment(initial_date).format("YYYY-MM-DD") <
            moment(first_active_op_theoric_date).format("YYYY-MM-DD");
    if ((all_ops_done || isCurrentOPDone) && of_delta)
        return return_delta
            ? +of_delta
            : moment(initial_date).add(of_delta, "days").format("YYYY-MM-DD");
    if (!current_date ||
        of_delta === undefined ||
        (!initial_date && !return_delta)) {
        return;
    }
    const todayDate = moment().format("YYYY-MM-DD");
    // While of_delta is the difference in days between the done date/planned date
    // and the ERP date, this one is the difference un days between the first ERP date
    // and the current one, and is used to preserve the lead time between OPs when
    // using the current date as the estimated end date for the first OP.
    const theoricDeltaDays = moment(initial_date).diff(moment(first_active_op_theoric_date).format("YYYY-MM-DD"), "days");
    const shouldUsePlannedDate = is_day_date_modified && first_active_op_planned_date;
    let deltaDays = of_delta;
    // First correct OP end date and delta
    if (first_active_op_theoric_date) {
        const possibleFirstOPEndDates = [todayDate];
        if (has_of_started)
            possibleFirstOPEndDates.push(moment(first_active_op_theoric_date)
                .add(of_delta, "days")
                .format("YYYY-MM-DD"));
        if (shouldUsePlannedDate)
            possibleFirstOPEndDates.push(moment(first_active_op_planned_date).format("YYYY-MM-DD"));
        const estimatedFirstOPEndDate = ___default.max(possibleFirstOPEndDates);
        deltaDays = moment(estimatedFirstOPEndDate).diff(moment(first_active_op_theoric_date).format("YYYY-MM-DD"), "days");
    }
    const estimatedEndDate = moment(current_date)
        .add(deltaDays, "days")
        .format("YYYY-MM-DD");
    if (!all_ops_done && !first_active_op_theoric_date) {
        const possibleCorrectedEstimatedEndDates = [estimatedEndDate, todayDate];
        if (shouldUsePlannedDate)
            possibleCorrectedEstimatedEndDates.push(moment(first_active_op_planned_date).format("YYYY-MM-DD"));
        const correctedEstimatedEndDate = ___default.max(possibleCorrectedEstimatedEndDates);
        deltaDays = moment(correctedEstimatedEndDate).diff(moment(current_date).format("YYYY-MM-DD"), "days");
    }
    const possibleDates = [
        moment(initial_date).add(deltaDays, "days").format("YYYY-MM-DD"),
        moment(todayDate).add(theoricDeltaDays, "days").format("YYYY-MM-DD"),
    ];
    if (is_day_date_modified && planned_date)
        possibleDates.push(moment(planned_date).format("YYYY-MM-DD"));
    const realEstimatedEndDate = ___default.max(possibleDates);
    if (return_delta)
        return moment(realEstimatedEndDate).diff(initial_date, "days");
    return realEstimatedEndDate;
};
/**
 *
 * @param initial_date The initial date of the OP
 * @param new_date The new date of the OP after all local changes
 * @param day_date The date of the OP before all local changes, after all previous changes
 * @param theoric_date //theoric end date of the OF, before all changes
 * @param first_active_op_theoric_date  propagated theoric date of the first active op, used for delay calculations on SchedulingOperationWrapper
 * @param first_active_op_planned_date  propagated scheduled date of the first active op, used for delay calculations on SchedulingOperationWrapper
 * @param real_date_delta operation card real_date_delta
 * @param all_ops_done
 */
const calculateOfDelay = (params) => {
    const { initial_date, new_date, day_date, theoric_date, first_active_op_theoric_date = "", first_active_op_planned_date = "", is_day_date_modified = false, real_date_delta = "", all_ops_done, nb_ops_done, } = params;
    // delay no longer receives corrections on OF termination
    if (all_ops_done && (real_date_delta || typeof real_date_delta === "number"))
        return real_date_delta;
    if (!initial_date)
        return 0;
    const estimatedDelta = getEstimatedEndDate(first_active_op_theoric_date, real_date_delta, {
        return_delta: true,
        initial_date: theoric_date,
        planned_date: day_date,
        first_active_op_theoric_date,
        first_active_op_planned_date,
        is_day_date_modified,
        all_ops_done,
        has_of_started: !!nb_ops_done,
    });
    if (estimatedDelta !== undefined)
        return estimatedDelta;
    const newOpDate = is_day_date_modified ? new_date !== null && new_date !== void 0 ? new_date : day_date : initial_date;
    const diffBtwnOpInitialAndNewDate = moment(newOpDate).diff(initial_date, "days");
    if (!theoric_date)
        return diffBtwnOpInitialAndNewDate;
    const newOfEndDate = moment(theoric_date)
        .add(diffBtwnOpInitialAndNewDate, "days")
        .format("YYYY-MM-DD");
    const ofDelay = moment(newOfEndDate).diff(theoric_date, "days");
    const daysLate = moment().diff(newOfEndDate, "days");
    if (daysLate <= 0)
        return ofDelay;
    return ___default.max([daysLate, ofDelay]);
};
// Lorsqu'on replanifie une OP pour minimiser le retard, on cherche à minimiser l'écart entre la date ERP de l'OP et sa date planifiée
// Afficher le retard de l'OF est incohérent dans ce cas, cf doc : https://www.notion.so/oplit/Pourquoi-le-tri-par-retard-donne-un-r-sultat-visuellement-incoh-rent-19b39c01e46280b6be2fd032f5c6bb3a
const calculateOpDelay = (params) => {
    const { day_date, new_date, erp_date } = params;
    const plannedDate = ___default.max([
        new_date || day_date || erp_date,
        moment().format(DATE_DEFAULT_FORMAT),
    ]);
    const erpDate = erp_date || undefined;
    return moment(plannedDate).diff(erpDate, "days");
};
/**
 * the `update.new_date` already includes calculations for the weekends offset
 * i.e. if we move one day in the future on a friday the `update.new_date` will be set on monday
 * creating a differential of 3 days whereas we only move one day
 * the `holidaysOffset` value below represents the amount of days that have been added to the original movement
 * (i.e. it would return 2 for the example above)
 * (¹)
 */
const calculateAvailableDates = (update, operation, finalOperationsList, opDate, calendar) => {
    let correctedOriginalEndDate = moment(update.new_date);
    const minDate = ___default.minBy(finalOperationsList, "day_date").day_date;
    const maxDate = ___default.maxBy(finalOperationsList, "day_date").day_date;
    // we need to cover potential further removed holidays/weekdays
    // minimum available dates length context : OPL-2508
    const length = Math.max(256, moment(maxDate).diff(minDate, "days") * 8);
    /**
     * we construct an array of dates within a range that are eligible
     * to be set for operations
     */
    const availableDates = Array.from({ length }, (_, i) => moment(minDate)
        // setting the same offset for past & future
        .subtract(length / 2, "days")
        .add(i, "days"))
        .filter((momentDate) => isWorkDay(momentDate, calendar))
        .map((momentDate) => momentDate.format("YYYY-MM-DD"));
    /**
     * we can either move by keyboard/arrows (1 absolute day diff) or by setting directly the new date
     * if we move with the keyboard and the next day is a weekend/holiday we force the movement in the past
     * otherwise, if we set a weekend/holiday through a datepicker, we want the next available date in the future to be chosen
     */
    const offset = update.new_date > operation.day_date ? 1 : -1;
    /**
     * we calculate the true end date for the original operation moved
     * this allows us to get the days differential that will be used
     * to determine the new dates for other operations
     */
    while (!isWorkDay(correctedOriginalEndDate, calendar)) {
        correctedOriginalEndDate = moment(correctedOriginalEndDate).add(offset, "days");
    }
    return {
        availableDates,
        correctedNewDate: correctedOriginalEndDate.format("YYYY-MM-DD"), // only for debugging
    };
};
const getAvailableNewDate = (newDate, isInFuture, dates) => {
    const filteredDates = dates.filter((date) => isInFuture ? date >= newDate : date <= newDate);
    if (!filteredDates.length)
        return null;
    return isInFuture
        ? filteredDates[0]
        : filteredDates[filteredDates.length - 1];
};
const getShiftFromDate = (date, shiftArray) => {
    const time = moment(date).format(TIME_DEFAULT_FORMAT);
    return (shiftArray.find((shift, index, shifts) => shift.start_time <= time &&
        (index === shiftArray.length - 1 ||
            time < shifts[index + 1].start_time)) || shiftArray[0]);
};
const spreadExtraInfoInOp = (operation) => {
    return Object.assign(Object.assign(Object.assign({}, (operation.extra_infos || {})), (operation.internal_extra_infos || {})), (operation || {}));
};
const getDefaultParametersSchedulingDisplayByClientID = (clientID) => {
    switch (clientID) {
        case "Cartier":
            return {
                info1: ["designation_article", null],
                info2: ["op_name", null],
                info3: ["customer", null],
            };
        // Sagemcom
        case "5hs5FtzBAp3zc2A0jji8":
            return {
                info1: ["ref_article", null],
                info2: ["of_name", null],
                info3: ["customer", null],
            };
        // Haas
        case "MUeFMwkxQ8sp4CgonYeq":
            return {
                info1: ["ref_article", null],
                info2: ["of_name", "customer"],
                info3: ["op_name", null],
            };
        // Parthenay
        case "kihJoGUywfHfI7abZWDH":
            return {
                info1: ["ref_article", null],
                info2: ["op_name", null],
                info3: ["of_name", null],
            };
        // Tesla Ordo
        case "Gsd8urfajIZSYuj3kkn7":
            return {
                info1: ["ref_article", null],
                info2: ["op_number", null],
                info3: ["op_name", null],
            };
        // Lisi
        case "g0E4nabmVzpLoQWUPnwq":
            return {
                info1: ["of_name", null],
                info2: ["reference", null],
                info3: ["ilot", null], // op.ilot || op.ligne_produit || "", //a changer pour l'op_name de l'OP actuelle de l'OF
            };
        // Jandon
        case "8GHQKvFXKSd4mHUqDJmB":
            return {
                info1: ["ref_article", null],
                info2: ["code_commande", "of_name"],
                info3: ["op_name", null],
            };
        // VCA
        case "QoGDqp6UoWoRbzsgKasn":
        case "3Mh1JfaDH8PbpjSRinNS":
            return {
                info1: ["ref_article", null],
                info2: ["op_name", null],
                info3: ["of_name", "theoric_date"],
            };
        // Vaucher
        case "Re4dpzZvU8ady8Ca3sxH":
            return {
                info1: ["ref_article", null],
                info2: ["of_id", null],
                info3: ["ref_article", null], // 2x ref_article ?
            };
        // Horlyne
        case "Horlyne":
            return {
                info1: ["ref_article", null],
                info2: ["plan_number", null],
                info3: ["of_id", null],
            };
        // Lisi VDR
        case "TT95CMEYHNnR2RpJOUw4":
            return {
                info1: ["of_name", null],
                info2: ["matiere", "forme_tete"],
                info3: ["diametre", "longueur_sous_tete"],
            };
        default:
            return {
                info1: ["ref_article", null],
                info2: ["op_name", null],
                info3: ["customer", null],
            };
    }
};
/**
 * creates a tag object from either a string representing its label or an object tag configuration (used to allow specific coloring)
 * since this function is shared, we don't have proper access to typing/constants
 */
const getDefaultTagObject = (tag) => {
    var _a;
    if (typeof tag !== "object")
        return {
            label: tag,
            color_name: "newPrimaryLight2",
        };
    const label = (tag === null || tag === void 0 ? void 0 : tag.label) || "";
    const color_name = (_a = tag === null || tag === void 0 ? void 0 : tag.color_name) !== null && _a !== void 0 ? _a : "newPrimaryLight2"; // SCHEDULING_TAGS_DEFAULT_COLOR
    return { label, color_name };
};
/**
 * The storage of tags has been changed from raw strings to objects inside the `scheduling_tags` collection.
 * This function is meant to convert those strings into objects and replace the existing tags with their underlying object's ID.
 * NB: the original tag string value is stored under the `label` key for the new object
 *
 * This function is meant to be used within a loop as such :
 * 1) get raw scheduling tags (outside of the loop)
 * 2) loop over objects
 * 3) call this function
 * 4) replace current object tags with return[0] and the raw scheduling tags with return[1]
 *
 * @param tagsArray The array of raw tags (strings) of the current object
 * @param schedulingTagsArray The array of all the `scheduling_tags` linked to the client
 * @param client_id The client_id in the current context (required to create objects in the collection)
 * @returns An array containing resp. the updated tags for the current object and the updated `scheduling_tags` (with new addition, to prevent re-fetching)
 */
function buildConvertRawTags(dbHelper) {
    return function (tagsArray, schedulingTagsArray, client_id, fakeInsert = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(tagsArray === null || tagsArray === void 0 ? void 0 : tagsArray.length))
                return [tagsArray, schedulingTagsArray];
            const clonedTagsArray = [...tagsArray];
            const clonedSchedulingTagsArray = [...schedulingTagsArray];
            const getTagFromKey = (tag, key) => clonedSchedulingTagsArray.find((t) => t[key] === tag && t.client_id === client_id);
            for (const [index, tag] of Object.entries(tagsArray)) {
                const typedTag = tag;
                if (getTagFromKey(typedTag, "id"))
                    continue;
                // if an object tag which label matches current tag already exists, we replace it by its id
                else if (getTagFromKey(typedTag, "label"))
                    clonedTagsArray.splice(+index, 1, ((_a = getTagFromKey(typedTag, "label")) === null || _a === void 0 ? void 0 : _a.id) || "");
                // we only create a scheduling_tag on not-blank tags
                else if (typedTag) {
                    // we create the object ..
                    const newTag = Object.assign(Object.assign({ id: yield dbHelper.getCollectionId("scheduling_tags") }, getDefaultTagObject(tag)), { client_id });
                    if (!fakeInsert) {
                        yield dbHelper.setDataToCollection("scheduling_tags", newTag.id, newTag, true);
                    }
                    // .. replace it ..
                    clonedTagsArray.splice(+index, 1, newTag.id);
                    // .. and add to the `all_scheduling_tags` array for next iteration
                    clonedSchedulingTagsArray.push(newTag);
                }
            }
            return [clonedTagsArray, clonedSchedulingTagsArray];
        });
    };
}
/**
 * used to have a format match between date updates\
 * this exists because of the present of the `shift_planning`
 * which formats the @originalDateUpdate as DATE_WITH_TIME_FORMAT
 * (vs DEFAULT_DATE_FORMAT otherwise)\
 * this solution has be to chosen to prevent passing an additional argument
 * and related calculus for adequate `shift_planning` determination
 */
function includeOriginalUpdateShiftPlanningInSiblingDate(originalDateUpdate, siblingOperationDateUpdate) {
    const isDateWithShift = moment(originalDateUpdate).format(DATE_DEFAULT_FORMAT).length <
        originalDateUpdate.length;
    const format = isDateWithShift ? DATE_WITH_TIME_FORMAT : DATE_DEFAULT_FORMAT;
    if (originalDateUpdate.length <= siblingOperationDateUpdate.length)
        return moment(siblingOperationDateUpdate).format(format);
    const withOriginalShift = `${siblingOperationDateUpdate}${originalDateUpdate.slice(siblingOperationDateUpdate.length)}`;
    return moment(withOriginalShift).format(DATE_WITH_TIME_FORMAT);
}
const pgOfQuery = ``;
function buildHandleStatusChangeFn(apiClient, dbHelper, getOFOperationList) {
    return function handleStatusChangeFn(userData, update, operation, simulation_id, operationsListParam, //the list of all operations of the OF
    extraParams) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { of_id } = operation;
            const { client_id } = userData || {};
            const { opIdsWithoutStatusEvent = [], hasSeveralStatusUpdate = false, ofUpdates = [], shouldKeepStatusOfPreviousOPs = false, shouldHandlePartialOPValidations = false, forcePendingOrderRecalculation = false, } = extraParams || {};
            // The base list of operations from the OF to be updated
            let newOperationsList;
            if (operationsListParam === null || operationsListParam === void 0 ? void 0 : operationsListParam.length)
                newOperationsList = operationsListParam;
            else {
                newOperationsList = yield getOFOperationList(client_id, of_id, simulation_id);
            }
            // The array of changes to be saved, if we don't save them immediately
            const returnedOps = [];
            // If the updated status is not available, on-going or done, and if there are several status updates, we don't need to update anything yet
            if (!["available", "on-going", "done"].includes(update.new_status) &&
                !hasSeveralStatusUpdate)
                return { newOperationsList, returnedOps };
            /**
             * badging system:
             * the following code's purpose is to update the list of all operations after one of them is marked as "done"
             * all previous operations within the list should also be updated as "done", and the one following the operation
             * parameter within the array is updated as "available"
             */
            // Get the last index of the updated OPs in sequence
            // This index can never be -1 because the operation from parameters is one of the array that constitutes what fills newOperationsList
            // We are just interested in whether or not this is the first of the list (so that we don't have to do any operation on the previous operations of the list)
            let lastIndexOfUpdatedOPSequence = ___default.findLastIndex(newOperationsList, ({ op_sequence }) => op_sequence == operation.op_sequence);
            if (lastIndexOfUpdatedOPSequence === -1) {
                lastIndexOfUpdatedOPSequence = newOperationsList.findIndex(({ op_id }) => op_id === operation.op_id);
            }
            // Get the first and last index of the next OPs in sequence
            // We want later on to be able to update all duplicated OPs if needed
            const firstIndexOfNextOPSequence = newOperationsList.findIndex((_, idx) => idx > lastIndexOfUpdatedOPSequence);
            let lastIndexOfNextOPSequence = ___default.findLastIndex(newOperationsList, ({ op_sequence }, idx) => {
                var _a;
                return op_sequence != null &&
                    idx >= firstIndexOfNextOPSequence &&
                    op_sequence ===
                        ((_a = newOperationsList[firstIndexOfNextOPSequence]) === null || _a === void 0 ? void 0 : _a.op_sequence);
            });
            if (lastIndexOfNextOPSequence === -1)
                lastIndexOfNextOPSequence = firstIndexOfNextOPSequence;
            const all_ops_done = newOperationsList
                .slice(lastIndexOfUpdatedOPSequence)
                .every((op) => (op === null || op === void 0 ? void 0 : op.status_done_at) || op.op_status === "done");
            // If passed OP is done, supercharge future ops with real_date & real_date_delta
            // and set next potentially duplicated OPs as available
            if (lastIndexOfUpdatedOPSequence > -1 && update.new_status === "done") {
                const { real_date: current_real_date, real_date_delta } = newOperationsList[lastIndexOfUpdatedOPSequence];
                const firstActiveOp = newOperationsList.find((x, idx) => idx > lastIndexOfUpdatedOPSequence &&
                    ![x.op_status, x.new_status].includes("done") &&
                    !x.status_done_at);
                let pending_order = 0;
                yield asyncForEach(newOperationsList, (op, currentIdx) => __awaiter(this, void 0, void 0, function* () {
                    const { op_id, initial_date, day_date, quantite_of: quantiteOF } = op;
                    let subupdate = {};
                    // We update all OPs up to the last of the updated OP sequence
                    if (currentIdx <= lastIndexOfUpdatedOPSequence) {
                        // All OPs up to the last of the updated OP sequence are set to done
                        if (!shouldKeepStatusOfPreviousOPs &&
                            (getOperationStatus(op) !== "done" ||
                                opIdsWithoutStatusEvent.includes(op.op_id || ""))) {
                            subupdate = Object.assign(Object.assign(Object.assign({}, subupdate), { new_status: "done", op_id }), (!shouldHandlePartialOPValidations && {
                                quantity_produced: quantiteOF || 0,
                            }));
                        }
                        // All OPs up to the last of the updated OP sequence get their first active date information updated
                        if ((firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) &&
                            (firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) !==
                                (firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.first_active_op_theoric_date)) {
                            subupdate = Object.assign(Object.assign({}, subupdate), { first_active_op_theoric_date: firstActiveOp.initial_date, first_active_op_planned_date: firstActiveOp.day_date, of_delta: real_date_delta, all_ops_done });
                        }
                        else {
                            subupdate = Object.assign(Object.assign({}, subupdate), { of_delta: real_date_delta, all_ops_done });
                        }
                    }
                    else {
                        if (current_real_date && ___default.isNumber(real_date_delta)) {
                            const real_date = moment(initial_date !== null && initial_date !== void 0 ? initial_date : day_date)
                                .add(real_date_delta, "days")
                                .format("YYYY-MM-DD");
                            subupdate = Object.assign(Object.assign({ real_date,
                                real_date_delta }, ((firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) && {
                                first_active_op_theoric_date: firstActiveOp.initial_date,
                                first_active_op_planned_date: firstActiveOp.day_date,
                            })), { of_delta: real_date_delta, all_ops_done });
                        }
                    }
                    // We update all OPs of the next sequence, taking into account the fact that there might be duplicates
                    if (!update.should_force_pending_status &&
                        currentIdx > lastIndexOfUpdatedOPSequence &&
                        currentIdx <= lastIndexOfNextOPSequence) {
                        const nextOpStatus = getOperationStatus(op);
                        if (nextOpStatus === "pending" ||
                            (nextOpStatus + "").includes("operation") ||
                            opIdsWithoutStatusEvent.includes(op.op_id || "")) {
                            subupdate.new_status = "available";
                            returnedOps.push(Object.assign(Object.assign({}, subupdate), { op_id: op.op_id || "", firestore_id: op.firestore_id || "" }));
                            return;
                        }
                        //Commentaire Souf : pour moi ici on doit laisser le else s'exécuter pour tous les autres cas, sinon par exemple pour une POF on n'enregistre pas le real_date dessus
                        // if (
                        //   ["available"].includes(nextOpStatus) &&
                        //   op.day_date === newOperationsList[opIdx].day_date
                        // )
                        else {
                            //special case to handle available future ops scheduled on same date
                            returnedOps.push(Object.assign(Object.assign({}, subupdate), { op_id: op.op_id || "", firestore_id: op.firestore_id || "" }));
                            return;
                        }
                        // We updated the pending order of all remaining OPs
                    }
                    else {
                        // We check if the current OP is a duplicate of the previous one before incrementing the pending order
                        const previousOP = newOperationsList.at(currentIdx - 1);
                        const isDuplicateOP = currentIdx > 0 &&
                            op.op_sequence != null &&
                            op.op_sequence === (previousOP === null || previousOP === void 0 ? void 0 : previousOP.op_sequence);
                        const isIndexCandidateForPendingOrder = update.should_force_pending_status
                            ? currentIdx >= lastIndexOfNextOPSequence
                            : currentIdx > lastIndexOfNextOPSequence;
                        const has_pending_order = isIndexCandidateForPendingOrder && op.op_status === "pending";
                        if (has_pending_order && !isDuplicateOP)
                            pending_order += 1;
                        returnedOps.push(Object.assign(Object.assign(Object.assign({}, subupdate), { op_id: op.op_id || "", firestore_id: op.firestore_id || "" }), (has_pending_order && {
                            internal_extra_infos: { pending_order },
                        })));
                        return;
                    }
                }));
                if (returnedOps.length) {
                    const searchArr = returnedOps;
                    newOperationsList = newOperationsList.map((op) => {
                        const match = searchArr.find((x) => (x === null || x === void 0 ? void 0 : x.op_id) === op.op_id);
                        if (!match)
                            return op;
                        return Object.assign(Object.assign({}, op), match);
                    });
                }
            }
            else if (lastIndexOfUpdatedOPSequence > -1 && hasSeveralStatusUpdate) {
                const updateByOpId = ___default.groupBy(ofUpdates.map(({ update }) => update), "op_id");
                const { real_date: current_real_date, real_date_delta } = newOperationsList[lastIndexOfUpdatedOPSequence];
                const firstIndexOfFirstActiveOP = newOperationsList.findIndex((x) => {
                    var _a;
                    const opUpdate = (_a = updateByOpId[x.op_id]) === null || _a === void 0 ? void 0 : _a[0];
                    const updateStatus = getOperationStatus(opUpdate);
                    if (updateStatus)
                        return updateStatus !== "done";
                    return x.op_status !== "done" || !x.status_done_at;
                });
                let lastIndexOfFirstActiveOP = ___default.findLastIndex(newOperationsList, ({ op_sequence }, idx) => {
                    var _a;
                    return op_sequence != null &&
                        idx >= firstIndexOfFirstActiveOP &&
                        op_sequence ===
                            ((_a = newOperationsList[firstIndexOfFirstActiveOP]) === null || _a === void 0 ? void 0 : _a.op_sequence);
                });
                if (lastIndexOfFirstActiveOP === -1)
                    lastIndexOfFirstActiveOP = firstIndexOfFirstActiveOP;
                const firstActiveOp = newOperationsList[firstIndexOfFirstActiveOP];
                let pending_order = 0;
                for (const [currentIdx, op] of newOperationsList.entries()) {
                    const { initial_date, day_date } = op;
                    const op_id = op.op_id;
                    const opUpdate = (_a = updateByOpId[op_id]) === null || _a === void 0 ? void 0 : _a[0];
                    let subupdate = {};
                    if (currentIdx < firstIndexOfFirstActiveOP) {
                        if ((firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) &&
                            (firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) !==
                                (firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.first_active_op_theoric_date)) {
                            subupdate = Object.assign(Object.assign({}, subupdate), { first_active_op_theoric_date: firstActiveOp.initial_date, first_active_op_planned_date: firstActiveOp.day_date });
                        }
                        subupdate = Object.assign(Object.assign({}, subupdate), { of_delta: real_date_delta, all_ops_done });
                    }
                    else {
                        if (current_real_date && ___default.isNumber(real_date_delta)) {
                            const real_date = moment(initial_date !== null && initial_date !== void 0 ? initial_date : day_date)
                                .add(real_date_delta, "days")
                                .format("YYYY-MM-DD");
                            subupdate = Object.assign(Object.assign({ real_date,
                                real_date_delta }, ((firstActiveOp === null || firstActiveOp === void 0 ? void 0 : firstActiveOp.initial_date) && {
                                first_active_op_theoric_date: firstActiveOp.initial_date,
                                first_active_op_planned_date: firstActiveOp.day_date,
                            })), { of_delta: real_date_delta, all_ops_done });
                        }
                    }
                    if (!update.should_force_pending_status &&
                        currentIdx >= firstIndexOfFirstActiveOP &&
                        currentIdx <= lastIndexOfFirstActiveOP) {
                        const nextOpStatus = getOperationStatus(opUpdate) || getOperationStatus(op);
                        if (["pending"].includes(nextOpStatus) ||
                            opIdsWithoutStatusEvent.includes(op_id))
                            subupdate.new_status = "available";
                    }
                    else {
                        // We check if the current OP is a duplicate of the previous one before incrementing the pending order
                        const previousOP = newOperationsList.at(currentIdx - 1);
                        const isDuplicateOP = currentIdx > 0 &&
                            op.op_sequence != null &&
                            op.op_sequence === (previousOP === null || previousOP === void 0 ? void 0 : previousOP.op_sequence);
                        const has_pending_order = currentIdx >= firstIndexOfFirstActiveOP;
                        if (has_pending_order) {
                            if (!isDuplicateOP)
                                pending_order += 1;
                            subupdate.new_status = "pending";
                            subupdate.internal_extra_infos = { pending_order };
                        }
                    }
                    returnedOps.push(Object.assign(Object.assign({}, subupdate), { op_id, firestore_id: op.firestore_id }));
                    continue;
                }
                if (returnedOps.length) {
                    const returnedOpsByOpId = ___default.groupBy(returnedOps, "op_id");
                    newOperationsList = newOperationsList.map((op) => {
                        var _a;
                        const match = (_a = returnedOpsByOpId[op.op_id]) === null || _a === void 0 ? void 0 : _a[0];
                        if (!match)
                            return op;
                        return Object.assign(Object.assign({}, op), match);
                    });
                }
            }
            else if (lastIndexOfUpdatedOPSequence > -1 &&
                forcePendingOrderRecalculation) {
                // A simple toggle to enforce pending order recalculation on the following OPs
                // Will not update OPs that are not pending, and will reset the pending order upon meeting one
                let pending_order = 0;
                for (const [currentIndex, currentOP] of newOperationsList.entries()) {
                    if (currentIndex <= lastIndexOfUpdatedOPSequence)
                        continue;
                    const op_id = currentOP.op_id;
                    let subupdate = {};
                    // We need the previous OP to check for duplicates
                    const previousOP = newOperationsList.at(currentIndex - 1);
                    const isDuplicateOP = currentIndex > 0 &&
                        currentOP.op_sequence != null &&
                        currentOP.op_sequence === (previousOP === null || previousOP === void 0 ? void 0 : previousOP.op_sequence);
                    // We reset the pending order if we meet an OP that isn't pending
                    if (!isDuplicateOP &&
                        previousOP &&
                        previousOP.op_status !== OF_STATUS.PENDING)
                        pending_order = 0;
                    const hasPendingOrder = currentOP.op_status === OF_STATUS.PENDING;
                    if (hasPendingOrder) {
                        if (!isDuplicateOP)
                            pending_order++;
                        subupdate.internal_extra_infos = { pending_order };
                    }
                    returnedOps.push(Object.assign(Object.assign({}, subupdate), { op_id }));
                    continue;
                }
                if (returnedOps.length) {
                    const returnedOpsByOpId = ___default.groupBy(returnedOps, "op_id");
                    newOperationsList = newOperationsList.map((op) => {
                        var _a;
                        const match = (_a = returnedOpsByOpId[op.op_id]) === null || _a === void 0 ? void 0 : _a[0];
                        if (!match)
                            return op;
                        return Object.assign(Object.assign({}, op), match);
                    });
                }
            }
            return { newOperationsList, returnedOps };
        });
    };
}
function isDoneOperation(operation) {
    return getOperationStatus(operation) === OF_STATUS.DONE;
}
/**
 * in the context of sub-ofs creation, newly-created operations with their `op_id` as concatenated values
 * are formatted with the new concatenation expression while operations from imports of old customers
 * are using the previous separator (_).
 * the below code ensures unicity between both formats
 */
function shouldUnifyOpID(operationsArray) {
    const hasFirstOPConcatExpr = operationsArray[0].op_id.includes(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION);
    for (let i = 1; i < operationsArray.length; i++) {
        const { op_id } = operationsArray[i];
        const hasCurrentOPConcatExpr = op_id.includes(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION);
        if (hasFirstOPConcatExpr !== hasCurrentOPConcatExpr)
            return true;
    }
    return false;
}
function getUnifiedOpID(opID) {
    return opID === null || opID === void 0 ? void 0 : opID.replaceAll(PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION, "_");
}
function parseDateToDMY(date) {
    if (!date)
        return "";
    return moment(date).format("DD/MM/YYYY");
}
function getContextualReadableImportParsingRuleValue(value) {
    return getReadableImportParsingRuleValue(value);
}
function parseReschedulingLogs(params, t) {
    const { ai_data_by_sector, reschedulingLogs, stations, pace_on_extra_field } = params;
    const rows = [];
    const parametersRows = [];
    //build the logs
    ai_data_by_sector.forEach((ai_sector_data) => {
        const { secteur_id, group_by, grouping_mesh, order, nb_calculation_weeks, target_charge_rate, force_min_pace, pace_groups, pace_mesh = "day", can_split_quantity_op, has_max_daily_capa, max_daily_capa, } = ai_sector_data;
        const sector = stations.find((poste) => poste.id === secteur_id);
        if (!sector) {
            rows.push(Object.assign(Object.assign({}, ai_sector_data), { text: `Le secteur avec l'identifiant ${secteur_id} ne semble plus exister dans Oplit` }));
            return;
        }
        const { parent_text } = sector;
        const sentences = [
            `Les paramètres de planification du secteur "${parent_text}" sont les suivants :`,
        ];
        if (order === null || order === void 0 ? void 0 : order.length) {
            sentences.push(`   - tri sur les variables suivantes :`);
            order.forEach((item) => {
                var _a, _b;
                const { model } = item;
                let fieldName;
                if (model === "of_delay") {
                    fieldName = t("Parametres.schedule_rules.priorities.of_delay");
                }
                else if (model === "op_delay") {
                    fieldName = t("Parametres.schedule_rules.priorities.op_delay");
                }
                else if (model === "stagnation") {
                    fieldName = t("Parametres.schedule_rules.priorities.waiting_time");
                }
                else if (model === "op_status") {
                    fieldName = t("Parametres.schedule_rules.priorities.op_status");
                }
                else if (model === "fast_track") {
                    fieldName = t("Parametres.schedule_rules.priorities.fast_track");
                }
                else {
                    fieldName = (_a = ofTooltipMatch.find((x) => x.field === item.model)) === null || _a === void 0 ? void 0 : _a.label;
                }
                if (!fieldName)
                    fieldName = item.model || "";
                const direction = item.direction === "custom"
                    ? `personnalisé : ${(_b = item.custom_directions) === null || _b === void 0 ? void 0 : _b.map((dir) => dir.value).join(", ")}`
                    : item.direction === "desc"
                        ? "décroissant"
                        : "croissant";
                sentences.push(`      - ${item.order}: "${fieldName}" (ordre ${direction})`);
            });
        }
        if (nb_calculation_weeks) {
            sentences.push(`   - ${t("Parametres.schedule_rules.replanning_mesh.title")} : ${nb_calculation_weeks}`);
        }
        if (target_charge_rate) {
            sentences.push(`   - ${t("Parametres.schedule_rules.target_charge_rate.title")} : ${target_charge_rate}% à la maille de la semaine`);
        }
        if (can_split_quantity_op) {
            sentences.push(`   - ${t("Parametres.schedule_rules.target_charge_rate.can_split_quantity_op")}`);
            if (has_max_daily_capa) {
                sentences.push(`   - ${t("Parametres.schedule_rules.target_charge_rate.has_max_daily_capa")}${max_daily_capa}`);
            }
        }
        if (pace_groups === null || pace_groups === void 0 ? void 0 : pace_groups.length) {
            sentences.push(`   - Règle utilisée pour le cadencement minimum : ${t(`ParametersSchedulingRulesConstraints.min_pace_${force_min_pace ? "enforcing" : "standard"}_rule`)}`);
            sentences.push(`   - ${t("Parametres.schedule_rules.pace_settings.title")}`);
            pace_groups.forEach((group) => {
                sentences.push(`      - "${group.oplit_name}" ${t("Simulation.in_unit_lowercase")} #${group.quantity_field} :`);
                group.options.forEach((option) => {
                    sentences.push(`         - "${option.value}" : ${t(`ParametersSchedulingRulesConstraints.pace_constraint_${option.constraint}`)} ${option.amount} ${t("global.by")} ${t(`ParametersSchedulingRulesConstraints.mesh_${pace_mesh}`).toLowerCase()}`);
                });
            });
        }
        if (group_by) {
            sentences.push(`   - ${t("Parametres.schedule_rules.automated_grouping_when_rescheduling.title")} par ${group_by} à une maille ${grouping_mesh ? t("scheduling." + grouping_mesh) : "inconnue"}`);
        }
        parametersRows.push(Object.assign(Object.assign({}, ai_sector_data), { text: sentences.join("\n") }));
    });
    //add sector name
    const mappedOps = reschedulingLogs.map((x) => {
        var _a;
        return (Object.assign(Object.assign({}, x), { secteur_name: x.secteur_id && ((_a = stations.find((y) => y.id === x.secteur_id)) === null || _a === void 0 ? void 0 : _a.parent_text) }));
    });
    //On affiche d'abord la position initiale de chaque OP après tri :
    const sortedOps = ___default.uniqBy(___default.sortBy(mappedOps.filter((x) => x.idx_after_sort != null), ({ idx_after_sort }) => +(idx_after_sort || 0)), "op_id");
    sortedOps.forEach((log) => {
        var _a;
        const { op_id, of_id, idx_after_sort, secteur_id, nb_ops_after_sort = 0, initial_date, erp_date, min_planned_date, } = log;
        const sentences = [];
        sentences.push(`date initiale = ${parseDateToDMY(initial_date)}`);
        if (erp_date)
            sentences.push(`date ERP = ${parseDateToDMY(erp_date)}`);
        if (min_planned_date)
            sentences.push(`date minimum de planification = ${parseDateToDMY(min_planned_date)}`);
        const sectorOrder = (_a = ai_data_by_sector.find((x) => x.secteur_id === secteur_id)) === null || _a === void 0 ? void 0 : _a.order;
        (sectorOrder || []).forEach((item) => {
            if (log[item.model] === undefined)
                return;
            sentences.push(`"${item.model}" = ${log[item.model]}`);
        });
        const detail = sentences.length ? ` (${sentences.join(", ")})` : "";
        rows.push(Object.assign(Object.assign({}, log), { text: `Après tri, l'OP ${getContextualReadableImportParsingRuleValue(op_id)} de l'OF ${of_id} est en position ${+(idx_after_sort || 0) + 1} / ${nb_ops_after_sort} ${detail}` }));
    });
    //On traite ensuite tout ce qui est fait dans la v1 (incl. 1.bis et 1_3)
    const v1Logs = mappedOps.filter((x) => x.file_number == 1);
    v1Logs.forEach((log) => {
        const { op_id, of_id, new_date, sort_version, initial_tx_charge, load, day_target_charge_rate, capa_value, final_tx_charge, sorted_delay_length, secteur_name, match_idx, nb_placed_ops_today, initial_load, pace_details = {}, leading_op, length_tracker, all_ops_in_future, min_date, split_load, use_erp_date, } = log;
        const sentences = [
            `L'OP ${op_id} de l'OF ${of_id || "inconnu"} est positionnée à la date du ${parseDate(new_date, false, false, DATE_WITH_DEFAULT_TIME_FORMAT)} sur le secteur "${secteur_name || "inconnu"}" : `,
        ];
        if (sort_version === "v1") {
            sentences.push(`   - position de l'OP dans la pile des OPs restant à traiter : ${+(match_idx || 0) + 1} / ${sorted_delay_length}`);
            sentences.push(`   - taux de charge initial : ${___default.round((initial_tx_charge || 0) * 100, 2)}%${nb_placed_ops_today == 0 && initial_load
                ? ` (La charge déjà positionnée sur cette journée via les opérations précédentes est de : ${initial_load})`
                : ""}`);
            sentences.push(`   - taux de charge cible : ${___default.round((day_target_charge_rate || 0) * 100, 2)}%`);
            sentences.push(`   - capacité du secteur : ${___default.round(capa_value || 0, 2)}`);
            sentences.push(`   - quantité de l'OP : ${___default.round(load || 0, 2)}${split_load ? ` (${___default.round(split_load || 0, 2)} d'inséré)` : ""}`);
            sentences.push(`   - taux de charge après insertion de l'OP : ${___default.round((final_tx_charge || 0) * 100, 2)}%`);
        }
        else if (sort_version === "v1_3" && pace_details) {
            Object.keys(pace_details).forEach((key) => {
                const { constraint, value_to_pace, qty_of_pace, previous_qty, extra_field_value, amount, pro_rata, } = pace_details[key];
                sentences.push(`   - valeur à cadender (${constraint}) de l'OP : "${value_to_pace}"`);
                sentences.push(`   - valeur précédemment utilisée pour le cadencement : ${___default.round(previous_qty, 2)}`);
                if (extra_field_value !== "none" && pace_on_extra_field) {
                    sentences.push(`   - Valeur du champ "${pace_on_extra_field}" utilisé pour cadencer : ${extra_field_value}`);
                }
                sentences.push(`   - valeur utilisée pour le cadencement de l'OP : ${___default.round(qty_of_pace, 2)}`);
                if (amount !== undefined) {
                    sentences.push(`   - valeur de référence pour le cadencement${pro_rata ? " avec pro-rata" : ""} : ${___default.round(amount, 2)}${pro_rata ? ` (initial : ${___default.round(amount / pro_rata, 2)})` : ""}`);
                }
            });
        }
        else if (sort_version === "after_max_date") {
            sentences.push(`   - il n'y avait plus de capacité disponible sur la période sélectionnée. Toutes les ${sorted_delay_length} OPs restantes ont été placées au dernier jour`);
        }
        else if (sort_version === "ops_from_ofs") {
            sentences.push(`   - la date ${use_erp_date ? "ERP" : "planifée"} est utilisée`);
            if ((leading_op === null || leading_op === void 0 ? void 0 : leading_op.op_id) !== op_id) {
                sentences.push(`   - l'OP ${leading_op === null || leading_op === void 0 ? void 0 : leading_op.op_id} du même OF ${of_id} a été placée à la date du ${parseDate(leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)}`);
                sentences.push(`   - Pour respecter la séquence de fabrication, l'OP ${op_id} a été placée à la date du ${parseDate(new_date)}`);
            }
            else if (new_date !== (leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)) {
                sentences.push(`   - Pour respecter la séquence de fabrication, cette OP a été déplacée par rapport à sa date initialement programmée au ${parseDate(leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)}`);
            }
            else {
                sentences.push(`   - Pas de changement par-rapport à la séquence initiale`);
            }
        }
        else if (sort_version === "timelag_disabled") {
            sentences.push(`   - cette OP n'a pas été traitée par l'algorithme de replanification`);
            if (length_tracker) {
                sentences.push(`   - l'algorithme s'est arrêté automatiquement car aucune OP n'a pu être placée pendant 3 périodes consécutives`);
            }
            else if (all_ops_in_future) {
                sentences.push(`   - à la fin de la première période de traitement, toutes les opérations restantes étaient dans le futur (date min = ${min_date})`);
                sentences.push(`   - l'algorithme s'est arrêté automatiquement dans ce cas`);
            }
            else {
                //ce cas ne devrait jamais arriver normalement
                sentences.push(`   - il ne restait plus aucune OP à placer`);
            }
        }
        rows.push(Object.assign(Object.assign({}, log), { text: sentences.join("\n") }));
    });
    //On traite enfin tout ce qui est fait dans la v2 (grouping)
    const v2Logs = mappedOps.filter((x) => x.file_number == 2);
    v2Logs.forEach((log) => {
        const { op_id, of_id, new_date, load, sort_version, secteur_name, op_idx, sorted_delay_length, date_from_v1, initial_load, initial_tx_charge, final_tx_charge, capa_value, is_last_day_of_maille, leading_op, day_target_charge_rate, group_by_value, } = log;
        const sentences = [
            `Après groupement, l'OP ${op_id} de l'OF ${of_id || "inconnu"} est positionnée à la date du ${parseDate(new_date, false, false, DATE_WITH_DEFAULT_TIME_FORMAT)} sur le secteur "${secteur_name || "inconnu"}" : `,
        ];
        if (sort_version === "v2") {
            if (is_last_day_of_maille) {
                sentences.push(`   - l'OP est placée sur le dernier jour de la semaine, on ignore le taux de charge dans ce cas`);
            }
            else {
                sentences.push(`   - position de l'OP dans la pile des OPs à traiter : ${(op_idx || 0) + 1} / ${sorted_delay_length || 0}`);
                sentences.push(`   - la date de l'OP après le tri initial était au ${parseDate(date_from_v1)}`);
                sentences.push(`   - taux de charge initial : ${___default.round((initial_tx_charge || 0) * 100, 2)}%${op_idx === 0 && initial_load
                    ? ` (La charge déjà positionnée sur cette journée via les opérations précédentes est de : ${initial_load})`
                    : ""}`);
                sentences.push(`   - taux de charge cible : ${___default.round((day_target_charge_rate || 0) * 100, 2)}%`);
                sentences.push(`   - capacité du secteur : ${___default.round(capa_value || 0, 2)}`);
                if (group_by_value)
                    sentences.push(`   - valeur du grouping : ${group_by_value}`);
            }
            sentences.push(`   - quantité de l'OP : ${___default.round(load || 0, 2)}`);
            sentences.push(`   - taux de charge après insertion de l'OP : ${___default.round((final_tx_charge || 0) * 100, 2)}%`);
        }
        else if (sort_version === "v2_ops_from_ofs") {
            if ((leading_op === null || leading_op === void 0 ? void 0 : leading_op.op_id) !== op_id) {
                sentences.push(`   - Après groupement, l'OP ${leading_op === null || leading_op === void 0 ? void 0 : leading_op.op_id} du même OF ${of_id} a été placée à la date du ${parseDate(leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)}`);
                sentences.push(`   - Pour respecter la séquence de fabrication, l'OP ${op_id} a été placée à la date du ${new_date}`);
            }
            else if (new_date !== (leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)) {
                sentences.push(`   - Pour respecter la séquence de fabrication, cette OP a été déplacée par rapport à sa date initialement programmée au ${parseDate(leading_op === null || leading_op === void 0 ? void 0 : leading_op.new_date)}`);
            }
            else {
                sentences.push(`   - Pas de changement par-rapport à la séquence initiale`);
            }
        }
        else if (sort_version === "v2_after_max_date") {
            sentences.push(`   - il n'y avait plus de capacité disponible sur la période sélectionnée. Toutes les ${sorted_delay_length} OPs restantes ont été placées au dernier jour`);
        }
        rows.push(Object.assign(Object.assign({}, log), { text: sentences.join("\n") }));
    });
    return { rows, parametersRows };
}
function getOperationStatus(operation) {
    var _a, _b;
    if (!operation)
        return "";
    return (_b = (_a = operation.new_status) !== null && _a !== void 0 ? _a : operation.op_status) !== null && _b !== void 0 ? _b : "";
}
function getOperationRealDateDelta(operation) {
    const { real_date, day_date, initial_date } = operation;
    if (!real_date)
        return null;
    return moment(real_date).diff(initial_date !== null && initial_date !== void 0 ? initial_date : day_date, "days");
}
function areSchedulingFiltersActive(filters) {
    return filters.some((filter) => { var _a; return ((_a = filter.selectedItem) === null || _a === void 0 ? void 0 : _a.length) > 0; });
}
function filterSchedulingOFs(operations, filters) {
    const filteredOps = filterSchedulingOperations(operations, filters);
    const OFIdsToExport = [
        ...new Set(filteredOps.map(({ of_id }) => of_id)),
    ];
    const opsByOf = ___default.groupBy(operations, "of_id");
    const allOpsForFilteredOfs = OFIdsToExport.flatMap((of_id) => opsByOf[of_id]);
    return allOpsForFilteredOfs;
}
function filterSchedulingOperations(operations, filters, { with_pending_order } = {
    with_pending_order: false,
}) {
    if (!areSchedulingFiltersActive(filters))
        return operations;
    const activeFilters = filters.filter((filter) => { var _a; return ((_a = filter.selectedItem) === null || _a === void 0 ? void 0 : _a.length) > 0; });
    const filteredOps = operations.filter((op) => activeFilters.every((f) => {
        var _a, _b, _c;
        if (!(f.id in op) &&
            !(f.id in (op.internal_extra_infos || {})) &&
            !(f.id in (op.extra_infos || {}))) {
            return f.selectedItem.includes("none_value");
        }
        if (f.id === "op_status") {
            // base statuses check
            const opStatus = getOperationStatus(op);
            const defaultTest = f.selectedItem.includes(opStatus);
            if (!with_pending_order)
                return defaultTest;
            if (opStatus !== OF_STATUS.PENDING && opStatus !== OF_STATUS.POF)
                return defaultTest;
            let opPendingOrder = ((_a = op === null || op === void 0 ? void 0 : op.internal_extra_infos) === null || _a === void 0 ? void 0 : _a.pending_order) || op.pending_order;
            if (opPendingOrder == null)
                return defaultTest;
            if (opPendingOrder > 10)
                opPendingOrder = -1;
            // with operation N-X check
            const isPendingOrderMatch = f.selectedItem.includes(opPendingOrder);
            return defaultTest || isPendingOrderMatch;
        }
        const opField = op[f.id] || ((_b = op.internal_extra_infos) === null || _b === void 0 ? void 0 : _b[f.id]) || ((_c = op.extra_infos) === null || _c === void 0 ? void 0 : _c[f.id]);
        // filtering array fields
        if (Array.isArray(opField))
            return (opField.some((item) => f.selectedItem.includes(item)) ||
                (f.selectedItem.includes("none_value") && !opField.length));
        if (typeof opField !== "string" && typeof opField !== "number")
            return false;
        return (f.selectedItem.includes(opField) ||
            (f.selectedItem.includes("none_value") && opField === ""));
    }));
    return filteredOps;
}
function formatSchedulingComponentQuantity(quantity) {
    if (!Number.isFinite(+quantity))
        return quantity;
    return +quantity.toFixed(SCHEDULING_COMPONENTS_QUANTITIES_DECIMALS);
}

const localSchedulingOpKeys = [
    "op_id",
    "of_id",
    "op_sequence",
    "simulation_id",
    "secteur_id",
    "of_name",
    "op_name",
    "site_id",
    "op_status",
    "day_date",
    "firestore_id",
    "status_done_at",
    "status_ongoing_at",
    "status_available_at",
    "initial_date",
    "real_date_delta",
    "real_date",
    "cross_simulation_id",
    "wip_order",
];
var ConwipTicketsGaugeFields;
(function (ConwipTicketsGaugeFields) {
    ConwipTicketsGaugeFields["Underload"] = "underload_tickets_count";
    ConwipTicketsGaugeFields["Ideal"] = "default_ideal_tickets_count";
    ConwipTicketsGaugeFields["OverloadModerate"] = "overload_moderate_tickets_count";
    ConwipTicketsGaugeFields["OverloadCritical"] = "overload_critical_tickets_count";
})(ConwipTicketsGaugeFields || (ConwipTicketsGaugeFields = {}));

var SchedulingComponentsEntryTypes;
(function (SchedulingComponentsEntryTypes) {
    SchedulingComponentsEntryTypes["CustomersOrders"] = "customers_orders";
    SchedulingComponentsEntryTypes["PurchaseOrders"] = "purchase_orders";
    SchedulingComponentsEntryTypes["OfDebit"] = "of_debit";
    SchedulingComponentsEntryTypes["OfCredit"] = "of_credit";
    SchedulingComponentsEntryTypes["PofDebit"] = "pof_debit";
    SchedulingComponentsEntryTypes["PofCredit"] = "pof_credit";
})(SchedulingComponentsEntryTypes || (SchedulingComponentsEntryTypes = {}));

var ImportFlowLogLevels;
(function (ImportFlowLogLevels) {
    ImportFlowLogLevels["INFO"] = "INFO";
    ImportFlowLogLevels["WARN"] = "WARN";
    ImportFlowLogLevels["ERROR"] = "ERROR";
})(ImportFlowLogLevels || (ImportFlowLogLevels = {}));

const isOpWithMinMaxDates = (op) => !!op.min_date && !!op.max_date;
const getMinMaxDateForPlacement = (op, startDate) => ({
    minDateForPlacement: _.max([op.min_date, startDate]),
    maxDateForPlacement: _.max([op.max_date, startDate]),
});
const mapOpsWithGanttRowIndex = (ops, startDate) => {
    const sortedOps = _.orderBy(ops, ["day_date", "op_order"]);
    let gr_idx = 0;
    let previousOpMaxDate;
    let shouldResetGrIdx = true;
    return sortedOps.map((op) => {
        if (!isOpWithMinMaxDates(op))
            return op;
        const { minDateForPlacement, maxDateForPlacement } = getMinMaxDateForPlacement(op, startDate);
        if (minDateForPlacement > startDate && shouldResetGrIdx) {
            gr_idx = 0;
            shouldResetGrIdx = false;
        }
        if (minDateForPlacement <= previousOpMaxDate)
            gr_idx++;
        previousOpMaxDate = maxDateForPlacement;
        const isBefore = op.max_date < startDate;
        if ((minDateForPlacement <= startDate || maxDateForPlacement <= startDate) &&
            !isBefore &&
            shouldResetGrIdx)
            shouldResetGrIdx = false;
        return Object.assign(Object.assign({}, op), { gr_idx });
    });
};

const slugOptions = {
    replacement: "_",
    lower: true,
    strict: true, // strip special characters except replacement, defaults to `false`
};
function buildFetchActiveMSD(dbHelper) {
    return function fetchActiveMSD(simulation, client_id) {
        return __awaiter(this, void 0, void 0, function* () {
            let active_msd;
            if (simulation === null || simulation === void 0 ? void 0 : simulation.msd_id) {
                active_msd = [
                    yield dbHelper.getDocFromCollection("msd", simulation.msd_id),
                ];
            }
            else {
                active_msd = yield dbHelper.getAllDataFromCollectionWithAll("msd", {
                    where: [
                        { field: "client_id", value: client_id, compare: "==" },
                        { field: "status", value: "active", compare: "==" },
                    ],
                    orderBy: [
                        { field: "start_date", direction: "desc" },
                        { field: "created_at", direction: "desc" },
                    ],
                    limit: 1,
                });
            }
            return active_msd[0] || {};
        });
    };
}
const inferDateFromExcel = (value) => {
    var _a;
    let newDate;
    if (value.includes("/")) {
        newDate = moment.utc(value, "DD/MM/YYYY");
    }
    else if (value.includes(".") && ((_a = value.match(/\./g)) === null || _a === void 0 ? void 0 : _a.length) === 2)
        newDate = moment.utc(value, "DD.MM.YYYY");
    else if (+value > 20000000)
        newDate = moment.utc(value, "YYYYMMDD");
    else if (value.includes("-"))
        newDate = moment.utc(value, "YYYY-MM-DD");
    else
        newDate = new Date(Math.round((+value - 25569) * 86400 * 1000));
    if (!moment(newDate).isValid())
        newDate = "1970-01-01";
    return moment(newDate).format("YYYY-MM-DD");
};
const parseIlot = (ilot) => {
    try {
        return toSafeString(ilot)
            .replace(/[_]| CK| CP/g, "")
            .trim();
    }
    catch (e) {
        console.error("parseIlot error", e);
        throw e;
    }
};
const FLOAT = /^-?(\d*\.?\d+|\d+\.?\d*)(e[-+]?\d+)?$/;
const PERCENT = /^\d+(\.\d+)?%$/;
function parseDynamicFunction(value, field) {
    if (!value || value === "" || ["#DIV/0!", "#N/A"].includes(value))
        return null;
    else if (field.includes("ilot") || field.includes("boucle"))
        return parseIlot(value);
    else if (["operation", "machine_tags"].includes(field)) {
        // Capitalize the first letter, lowercase the rest
        const trimmed = value.trim();
        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    }
    else if (["first_name", "last_name", "username", "operateurs"].includes(field)) {
        // Capitalize every first letter, lowercase the rest
        const trimmed = value.trim();
        return trimmed.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    }
    else if (field.includes("date") && field !== "updated") {
        const newDate = inferDateFromExcel(value);
        return newDate;
    }
    else if (FLOAT.test(value.replace(/\s/g, "").replace(/,/g, "."))) {
        const floatLikeValue = value.replace(/\s/g, "").replace(/,/g, ".");
        const floatValue = parseFloat(floatLikeValue);
        return Number.isFinite(floatValue) ? floatValue : floatLikeValue;
    }
    else if (PERCENT.test(value))
        return parseFloat(value) / 100.0;
    else if (["true", "TRUE", "VRAI"].includes(value))
        return true;
    else if (["false", "FALSE", "FAUX"].includes(value))
        return false;
    else
        return value.trim();
}
/**
 * Build a function that transforms headers into slugs by :
 * - slugifying the header
 * - removing all non-alphanumeric characters
 * - adding an index if the header is a duplicate
 * The headerTransformFunction is specially designed to work as a PapaParse transformHeader function
 * @returns {HeaderTransformFunction} headerTransformFunction : (header: string, index: number) => string
 */
function buildHeaderTransformFunction() {
    const previousHeaders = {};
    return (header, index) => {
        let newHeader = slugify(header, slugOptions).replace(/[^a-zA-Z0-9_]/g, "");
        if ((previousHeaders === null || previousHeaders === void 0 ? void 0 : previousHeaders[newHeader]) && previousHeaders[newHeader] != index) {
            newHeader += "_" + index;
        }
        previousHeaders[newHeader] = index;
        return newHeader;
    };
}
/**
 * Mapping of the import type to the parsing type
 */
const parsingTypeMapping = {
    ofs: "LOAD",
    prod: "PROD",
    cmj: "CMJ",
    nomenclatures: "NOMENCLATURES",
    gammes: "GAMMES",
    initial_stock: "WIP_STOCKS",
    initial_stock_pf: "FP_STOCKS",
    purchase_orders: "PURCHASE_ORDERS",
    customers_orders: "CUSTOMERS_ORDERS",
    techno_families: "TECHNO_FAMILIES",
    components_initial_stock: "COMPONENTS_INITIAL_STOCK",
};
function getClientParsingTypeMapping(clientParameters) {
    const { has_module_stocks } = clientParameters || {};
    if (!has_module_stocks)
        return parsingTypeMapping;
    return Object.assign(Object.assign({}, parsingTypeMapping), { ofs: "LOAD_STOCKS", prod: "PROD_STOCKS" });
}

function buildGetMSDData(dbHelper) {
    return function getMSDData(client_id, activeSector, active_msd, field = "secteur_id", type = "capa") {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const msd_data = yield dbHelper.getAllDataFromCollectionWithAll(`msd_${type === "charge" ? "charge_" : ""}data`, {
                where: [
                    {
                        field: "client_id",
                        value: client_id,
                        compare: "==",
                    },
                    {
                        field: field,
                        value: activeSector.id,
                        compare: "==",
                    },
                    {
                        field: "level",
                        value: ((_a = activeSector === null || activeSector === void 0 ? void 0 : activeSector.level) !== null && _a !== void 0 ? _a : 0) / 1,
                        compare: ">=",
                    },
                    {
                        field: "msd_id",
                        value: active_msd.id,
                        compare: "==",
                    },
                ],
                orderBy: [
                    { field: "level", direction: "asc" },
                    { field: "created_at", direction: "desc" },
                ],
            });
            /**
             * NB: we use `orderBy` here instead than within the query above because the firebase index isn't created
             */
            const sortedMSDData = ___default.orderBy(msd_data, [(o) => +(o.level || 0), (o) => parseDate(o.created_at)], ["asc", "desc"]);
            return sortedMSDData;
        });
    };
}

const allFields = [
    {
        label: "periode",
        model: "periode",
    },
];
function buildGetEvent(dbHelper) {
    return function getEvent(passed_event, client_id = "") {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const clientId = client_id || passed_event.client_id;
            const promises = [];
            promises.push(dbHelper.getDocFromCollection("events", passed_event.id));
            //get the children as well
            if (!((_a = passed_event.children) === null || _a === void 0 ? void 0 : _a.length) ||
                typeof passed_event.children[0] === "string") {
                promises.push(dbHelper.getAllDataFromCollectionWithAll("events", Object.assign({ childrenPath: [passed_event.id, "children"] }, (clientId && {
                    where: [{ field: "client_id", compare: "==", value: clientId }],
                }))));
            }
            const [event, children] = (yield Promise.all(promises));
            if (!event)
                return;
            if (children === null || children === void 0 ? void 0 : children.length) {
                if (((_b = passed_event.children) === null || _b === void 0 ? void 0 : _b.length) &&
                    typeof passed_event.children[0] === "string") {
                    //we try here to keep the order of the children as saved in the db
                    event.children = passed_event.children
                        .map((id) => children.find((child) => child.id === id))
                        .filter(Boolean);
                }
                else
                    event.children = children;
            }
            //backup
            if (typeof ((_c = event.children) === null || _c === void 0 ? void 0 : _c[0]) === "string" &&
                ((_d = passed_event.children) === null || _d === void 0 ? void 0 : _d.length) &&
                typeof ((_e = passed_event.children) === null || _e === void 0 ? void 0 : _e[0]) !== "string")
                event.children = passed_event.children;
            return event;
        });
    };
}
function getImportantKeys(initial, parametres_msd = {}, idType = "") {
    var _a;
    const cleaned = {};
    [...levelCorresp, { type: "parent" }].forEach((level) => {
        if (initial[level.type + "_id"]) {
            cleaned[level.type + "_id"] = initial[level.type + "_id"];
            cleaned[level.type + "_name"] = initial[level.type + "_name"] || null;
        }
    });
    [...((parametres_msd === null || parametres_msd === void 0 ? void 0 : parametres_msd.fields) || []), ...allFields].forEach((field) => {
        if (initial[field.model] !== undefined && !isNaN(initial[field.model]))
            cleaned[field.model] = initial[field.model];
    });
    const parent_level = initial.parent_level;
    if (parent_level) {
        cleaned.parent_level = {
            collection: parent_level.collection || null,
            type: parent_level.type || null,
            level: parent_level.level || null,
        };
    }
    cleaned.collection = initial.collection || null;
    cleaned.level = _.get(initial, "level", null); //_ to handle 0 case
    cleaned.type = initial.type || null;
    if (idType !== "secteur")
        cleaned.id = initial.id || initial.secteur_id || null;
    if (idType !== "secteur")
        cleaned.name = initial.name || initial.secteur_name || null;
    if (idType !== "id")
        cleaned.secteur_id = initial.secteur_id || initial.id || null;
    if (idType !== "id")
        cleaned.secteur_name = initial.secteur_name || initial.name || null;
    if (initial.isComputedWChildren)
        cleaned.isComputedWChildren = initial.isComputedWChildren || false;
    if (initial.is_machine)
        cleaned.is_machine = true;
    if (initial.is_operator)
        cleaned.is_operator = true;
    if (initial.is_tag)
        cleaned.is_tag = true;
    if (initial.is_filled)
        cleaned.is_filled = true;
    if (initial.msd_secteur_id)
        cleaned.msd_secteur_id = initial.msd_secteur_id;
    if (initial.msd_level !== undefined)
        cleaned.msd_level = initial.msd_level;
    if (initial.datelancement !== undefined)
        cleaned.datelancement = initial.datelancement;
    if ((_a = initial.periods) === null || _a === void 0 ? void 0 : _a.length)
        cleaned.periods = initial.periods;
    return cleaned;
}

/**
 * /!\ Implicit behavior: we do not override a value that already exists in the target object.
 * E.G: if the object has a "op_id" key, and another key that is translated to "op_id", the second value will be ignored.
 * @param data An array of objects to be mapped
 * @param clientNameByOplitHeader the raw translation mapping
 * @param bypassMapping fields that are not to be translated
 * @param keepUnmappedFields fields to be included even if they are not in the translation mapping nor in the bypass list. "all" to include all fields
 * @returns
 */
function translateRawExportedOplitHeaders(data, clientNameByOplitHeader, bypassMapping, keepUnmappedFields = []) {
    return data.map((row) => {
        var _a;
        var _b;
        const newRow = {};
        for (const [oplitInternalHeader, value] of Object.entries(row)) {
            const clientHeader = translateExportedField(oplitInternalHeader, clientNameByOplitHeader, bypassMapping);
            if (clientHeader === null &&
                keepUnmappedFields !== "all" &&
                !keepUnmappedFields.includes(oplitInternalHeader))
                continue;
            (_a = newRow[_b = clientHeader !== null && clientHeader !== void 0 ? clientHeader : oplitInternalHeader]) !== null && _a !== void 0 ? _a : (newRow[_b] = value);
        }
        return newRow;
    });
}
function translateExportedField(field, mapping, bypass, keepUnmappedFields = []) {
    const translated = bypass.includes(field) ? field : mapping[field];
    return (translated !== null && translated !== void 0 ? translated : (keepUnmappedFields === "all" || keepUnmappedFields.includes(field)
        ? field
        : null));
}

/**
 * @file Contains utils to help with sector manipulation and fetching (eg. getting all lowest level children).
 * @author Bastien FROMENT
 */
/**
 * Checks if a given sector-like object is valid.
 * @param sector Any sector-like object containing at least an id, collection and type.
 * @return Returns `true` if the passed sector-like object has an id, a collection, a type and an active status, `false` otherwise.
 */
function isSectorValid(sector) {
    return (!!sector &&
        !!sector.id &&
        !!sector.collection &&
        !!sector.type &&
        !["removed", "deleted"].includes(sector.status || ""));
}
/**
 * Checks both if the given sector is active and valid, and if it has active children.
 * @param sector Any sector-like object containing at least an id, collection and type.
 * @return Returns `true` if the passed sector-like object has active children with both an id and collection/type, `false` otherwise.
 */
function hasChildren(sector) {
    var _a, _b;
    return (isSectorValid(sector) &&
        !!((_b = (_a = sector.children) === null || _a === void 0 ? void 0 : _a.filter((child) => isSectorValid(child))) === null || _b === void 0 ? void 0 : _b.length));
}
/**
 * Gets all the lowest level children starting from the given sector-like object.
 * @param sector Any sector-like object containing at least an id, collection and type.
 * @param stations An array containing all sectors to be searched (most commonly found in the main store).
 * @return Returns an array containing all of the lowest level full children descending from the given sector.
 */
function getLowestLevelChildren(sector, stations) {
    if (!stations.length)
        return [];
    if (!hasChildren(sector))
        return [sector];
    const children = [];
    const getChildrenRecursively = (parent) => {
        var _a;
        const fullSector = stations.find(({ id }) => id === parent.id);
        if (!fullSector || !isSectorValid(fullSector))
            return;
        if (!hasChildren(fullSector))
            return children.push(fullSector);
        (_a = fullSector.children) === null || _a === void 0 ? void 0 : _a.forEach((child) => getChildrenRecursively(child));
    };
    getChildrenRecursively(sector);
    return children;
}
function buildGetLowestLevelChildrenFromSingleSector(dbHelper) {
    return function getLowestLevelChildrenFromSingleSector(sector, clientId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!hasChildren(sector))
                return [sector];
            const sectors = yield dbHelper.getAllDataFromCollectionWithAll("sectors", {
                where: [{ field: "client_id", value: clientId, compare: "==" }],
            });
            if (!sectors.length)
                return [];
            const children = [];
            const getChildrenRecursively = (parent) => {
                var _a;
                const fullSector = sectors.find(({ id }) => id === parent.id);
                if (!fullSector || !isSectorValid(fullSector))
                    return;
                if (!hasChildren(fullSector))
                    return children.push(fullSector);
                (_a = fullSector.children) === null || _a === void 0 ? void 0 : _a.forEach((child) => getChildrenRecursively(child));
            };
            getChildrenRecursively(sector);
            return children;
        });
    };
}
/**
 * creates pg `internal_extra_infos` from arbitrary keys in firebase `sectors` collection
 */
function buildSectorInternalExtraInfos(fbSector) {
    const { should_count_entries_for_quantity } = fbSector;
    if (!should_count_entries_for_quantity)
        return {};
    return {
        should_count_entries_for_quantity,
    };
}
function isEntryOfTeam(entry, team) {
    var _a;
    if (!((_a = entry.teams) === null || _a === void 0 ? void 0 : _a.length))
        return true;
    return entry.teams.some(({ id }) => id === (team === null || team === void 0 ? void 0 : team.id));
}

const dailyProdKeys = [
    "client_id",
    "import_id",
    "secteur_id",
    "of_id",
    "day_date",
    "prod",
    "collection",
    "type",
    "is_weight",
    "data_source",
    "updated_at",
    "from_children",
    "real_of_id",
    "op_status",
    "quantite_of",
    "quantite_op_produite",
    "extra_infos",
    "partition_key",
    "quantite_op_rebut",
    "quantite_of_disponible",
];
const dailyProdConcatenatedKeys = [
    "client_id",
    "import_id",
    "secteur_id",
    "op_id",
    "of_id",
    "day_date",
    "quantite_op_produite",
    "quantite_of_produite",
    "collection",
    "type",
    "updated_at",
    "op_status",
    "quantite_of",
    "ref_article",
    "op_sequence",
    "extra_infos",
    "first_inserted_at",
    "last_inserted_at",
    "quantite_op_rebut",
    "quantite_of_disponible",
];

var ApiBackend;
(function (ApiBackend) {
    ApiBackend["DATA"] = "data";
    ApiBackend["FRONT"] = "front";
    ApiBackend["INTERNAL"] = "internal";
    ApiBackend["IMPORT"] = "import";
})(ApiBackend || (ApiBackend = {}));

class Result {
    static success(payload) {
        return [payload, null];
    }
    static error(error) {
        return [null, error];
    }
    static errorWithContext(error, errorContext) {
        return [errorContext, error];
    }
    static isError(result) {
        return result[1] !== null;
    }
    static isSuccess(result) {
        return result[1] === null;
    }
    static unwrap(result, errorCallback = rethrow) {
        if (Result.isSuccess(result)) {
            return result[0];
        }
        errorCallback(result[1], result[0]);
        return null;
    }
}
function rethrow(err, ctx) {
    throw err;
}

function getArticleOperationKey(operation) {
    const { article_id, secteur_id, num_sequence } = operation;
    return [article_id, secteur_id, num_sequence].join("__");
}
/**
 * removes products that would not be updated by a demand-type prod. obj. adjustment
 * and maps them with only the necessary keys for further logic
 */
function cleanOriginalProducts(products, type) {
    let filteredProducts = products;
    /**
     * for the "demand" adjustment type we remove products that have no `demande`
     * nor `objective` to accelerate the process; these keys are sent in the frontend through #getStocksTable
     * we also preserve products with parents as they have dependencies on products we may not have information on
     */
    if (type === STOCKS_PROD_OBJ_TYPES.DEMAND)
        filteredProducts = products.filter(({ objective, demande, parent_article_names }) => {
            if (!(demande === 0 && objective === 0))
                return true;
            return (parent_article_names || []).length > 0;
        });
    /**
     * we map `products` with only the necessary keys for further logic\
     * this is made to reduce the storage in the firestore `jobs` table\
     * NB: the `is_semi_fini` key usage being different between the frontend and the backend,
     *     it has to remain filtered before processing after this function
     */
    const cleanedProducts = filteredProducts.map(({ article_id, article_name, secteur_id, num_sequence, parent_article_names,
    // is_semi_fini,
     }) => ({
        article_id,
        article_name,
        secteur_id,
        num_sequence,
        parent_article_names,
    }));
    return cleanedProducts;
}
/**
 * in the #adjustProdObjective function we treat the update by batches
 * for edge-cases with an update over a long period of time on many production range items\
 * this function returns how many articles should be treated per iteration
 * - MAX_OPERATIONS_PER_BATCH represents the maximum target number of rows to treat for each iteration
 * - 2 is a coefficient added because of the creation of a stock AND a demand event for other operations (prior to the one originally updated) in the production range
 */
function getAdjustProdObjectiveChunkSize(meanOperationsCount) {
    return Math.max(Math.floor(MAX_OPERATIONS_PER_BATCH / (meanOperationsCount * 2)), 1);
}

export { ALL_OPLIT_MODULES, ALL_TABLE_OF_HEADER_KEYS, ApiBackend, ConwipTicketsGaugeFields, DATE_DEFAULT_FORMAT, DATE_EXPORT_TO_ERP_FORMAT, DATE_FORMATS, DATE_WITH_DEFAULT_TIME_FORMAT, DATE_WITH_TIME_EXPORT_TO_ERP_FORMAT, DATE_WITH_TIME_FORMAT, DEFAULT_LOAD_UNITS, DEFAULT_PAST_START_DATE, DEFAULT_TABLE_OF_HEADER_KEYS, EVENTS_NEW_ID_PREFIX, EXPORT_LOAD_BYPASS_MAPPING, EXPORT_LOAD_PARSING_IDENTIFIER_MAPPING, EXPORT_PROD_BYPASS_MAPPING, EXPORT_PROD_PARSING_IDENTIFIER_MAPPING, EXPORT_SCHEDULING_LOAD_DEFAULT_MAPPING, FAST_TRACK, IMPORT, IMPORT_PARSING_FIELD_FORMULA_ITEM_TYPES, IMPORT_PARSING_FUNCTIONS, IMPORT_PARSING_OPERATIONS, IMPORT_TYPES, ImportFlowLogLevels, JoursFeries, LOCALES, MAIN_OPLIT_MODULES, MAP_TABLE_MODULE_ROUTE_NAME, MAX_OPERATIONS_PER_BATCH, OFS_SEARCHABLE_FIELDS, OF_STATUS, OPLIT_SUPPORT_EMAIL, PARAMETERS_IMPORT_PARSING_RULES_CONCATENATION_EXPRESSION, PENDING_ORDERS_PARAMS, PdpLoadEventStatus, PdpLoadEventType, Result, SCHEDULING_COMPONENTS_QUANTITIES_DECIMALS, SECONDARY_OPLIT_MODULES, STOCKS_PROD_OBJ_TYPES, SchedulingComponentsEntryTypes, TABLE_OF_NEEDED_SUBKEYS, TIME_DEFAULT_FORMAT, TIME_WITH_SECONDS_FORMAT, USER_ROLES, addedLoadTableKeys, addedRawLoadTableKeys, areSchedulingFiltersActive, asyncForEach, basePdpLoadEventKeys, bigqueryCommandesFields, bigqueryOfFields, booleanLoadEventKeys, buildConvertRawTags, buildFetchActiveMSD, buildGetEvent, buildGetLowestLevelChildrenFromSingleSector, buildGetMSDData, buildHandleStatusChangeFn, buildHeaderTransformFunction, buildRecursivelySectorTree, buildSectorInternalExtraInfos, calculateAvailableDates, calculateOfDelay, calculateOpDelay, canHaveEmptyStringValue, capitalizeFirstLetter, cleanNullishValuesInObject, cleanOriginalProducts, computeChipColor, computeImpact, computeInitials, computePlaceholderColor, dailyLoadConcatenatedKeys, dailyLoadKeys, dailyLoadKeysByType, dailyLoadPrimaryKeysFields, dailyProdConcatenatedKeys, dailyProdKeys, dailyRawLoadConcatenatedFromFiles, dailyRawLoadConcatenatedKeys, dailyRawLoadKeys, dailyRawLoadPrimaryKeysFields, datesFromPeriod, divideAndConquer, errorFormatter, escapeRegExp, evaluateCapa, filterSchedulingOFs, filterSchedulingOperations, filterSimulationsOnTeam, findRecursively, formatDate, formatSchedulingComponentQuantity, getAdjustProdObjectiveChunkSize, getAllChildrenFrom, getArticleOperationKey, getAvailableNewDate, getClientParsingTypeMapping, getConditionalQuantity, getDatesFromPeriod, getDaysArrayBetweenStartEnd, getDefaultParametersSchedulingDisplayByClientID, getDefaultTagObject, getDisplayedIdentifierFromConcatenatedValues, getDisplayedPeriod, getEndOfDateWithTime, getEstimatedEndDate, getFirstAssignation, getImportantKeys, getLevelCorrespFieldsForSector, getLowestLevelChildren, getMaxStatus, getMaxStatusFromConcatenatedStatuses, getMergedSchedulingOp, getMinMaxDateForPlacement, getMissingDays, getNewDateFromShift, getNextAvailableDate, getNextAvailableDateWithWorkdayCount, getOFDisplayedQuantityKeys, getOFUnit, getOPUnit, getObjectDiff, getOffDays, getOperationRealDateDelta, getOperationStatus, getOperationsAggregatedQuantities, getOperatorsKeys, getPeopleOrgaFromPeriodArray, getReadableImportParsingRuleValue, getSectorCalendar, getSectorKeys, getSectorOrderedCalendars, getShiftFromDate, getSortedPerimeters, getSortedUniqCalendarsItems, getStandardDailyLoadsFromSpreadOnes, getStartOfDateWithTime, getStoredIdentifierFromConcatenatedValues, getTimestamp, getUnifiedOpID, getValueInOpOrExtraInfos, getWorkDays, getWorkdaysArrayBetweenStartEnd, hasChildren, hasSectorAutoOrga, hasSomeLoadImportIdObj, hasWorkdaysPeriod, includeOriginalUpdateShiftPlanningInSiblingDate, inferDateFromExcel, isDoneOperation, isEntryOfTeam, isHoliday, isOpWithMinMaxDates, isPastDate, isSafeSectorNameMatching, isSectorValid, isUnitKilos, isWeekend, isWorkDay, levelCorresp, levelCorrespWithGroup, loadEventKeys, loadEventPrimaryKeysFields, loadEventRowKeys, loadInternalExtraInfosKeys, loadOfsKeys, localSchedulingOpKeys, makeOPsUnique, mapArrayWithOrder, mapOpsWithGanttRowIndex, merca_levels, nbWorkDays, ofTooltipMatch, opStatusList, parseDate, parseDynamicFunction, parseIlot, parseMaille, parsePeriode, parseReschedulingLogs, parsingTypeMapping, periodeToText, pgOfQuery, pgOpsMapFn, prepareDailyLoadData, prepareDailyRawLoadData, prepareLoadEventData, prepareLoadEventRowData, rawLoadInternalExtraInfosKeys, reverseLevelCorresp, reverseLevelCorrespWithGroup, roundNumberIfDefined, schedulingEventFields, searchSanitizedSubstring, searchSubstring, shouldUnifyOpID, simplifySector, sleep, slugOptions, spreadExtraInfoInOp, stockEventsRowsKeys, toFinite, toSafeString, translateExportedField, translateRawExportedOplitHeaders, updatableDailyLoadKeys, updatableDailyRawLoadKeys, updatableLoadEventKeys, updatableLoadOfsKeys, updatablePdpLoadEventKeys, updateExportedDateFormatInPlace, validateEmail, workDaysSum, workdayCountAndWorkdaysToDayCount, workdayCountToDayCount, yearAndWeekArrayFromPeriod };
